/*
 * ContactsPage Component
 *
 * Features:
 * - State and City dropdowns with API integration
 * - Phone number input with country code support
 * - Dynamic contact statistics
 * - Advanced filtering with state/city selection
 *
 * Required Package:
 * npm install react-phone-input-2
 *
 * API Endpoints used:
 * - POST /statelist - Get list of states
 * - POST /citylist - Get list of cities by state ID
 * - POST /addressbook/users - Get contacts from address book
 * - POST /contact/add - Add new contact
 * - POST /addressbook/user/update - Update existing user
 * - POST /addressbook/user/delete - Delete single user
 * - POST /addressbook/delete - Delete address book
 * - POST /contact/upload-excel-addressbook - Bulk upload contacts with address book
 */

import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Badge,
  Row,
  Col,
  Statistic,
  Dropdown,
  Menu,
  Modal,
  Form,
  Select,
  Upload,
  message,
  Divider,
  Avatar,
  Typography,
  Alert,
  Checkbox,
  Spin,
  Radio,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
  LoadingOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EditOutlined,
  DeleteOutlined,
  FormOutlined,
  DeleteFilled,
  MoreOutlined,
  ExportOutlined,
  FilterOutlined,
  TeamOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
  InboxOutlined,
  DownloadOutlined,
  MessageOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  TableOutlined,
  TagOutlined,
  StarOutlined,
  HeartOutlined,
  HomeOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../../../context/authContext";
import { useContacts } from "../../../../context/contactsContext";
import { PhoneCall, MailOpen, SquarePen, Trash2 } from "lucide-react";
import WriteGuard from "../../../../components/WriteGuard";
import axiosConfig from "../../../../config/axiosConfig";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { filter } from "lodash";
import * as XLSX from "xlsx";
import "./ContactsPage.css";
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import { Steps } from "antd";

// Add custom styles for the address book dropdown
const addressBookSelectStyle = {
  ".ant-select-selector:hover": {
    borderColor: "#2b8cee !important",
  },
  ".ant-select-selector:focus": {
    borderColor: "#2b8cee !important",
    boxShadow: "0 0 0 2px rgba(43, 140, 238, 0.15) !important",
  },
  ".ant-select-focused .ant-select-selector": {
    borderColor: "#2b8cee !important",
    boxShadow: "0 0 0 2px rgba(43, 140, 238, 0.15) !important",
  },
};

const { Step } = Steps;

const { Option } = Select;
const { Dragger } = Upload;
const { Text } = Typography;

// ── Skeleton Components (defined outside main component for performance) ──

const SKELETON_GRAD =
  "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)";
const SKELETON_GRAD_DARK =
  "linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)";
const SKEL_ANIM = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ? "none"
  : "skeleton-loading 1s infinite ease-in-out";

const StatsSkeleton = React.memo(() => (
  <Row gutter={16} style={{ marginBottom: 24 }}>
    {[0, 1, 2, 3].map((i) => (
      <Col xs={24} sm={12} lg={6} key={i}>
        <Card className="contacts-card">
          <div style={{ height: 60, background: SKELETON_GRAD, borderRadius: 8, animation: SKEL_ANIM }} />
        </Card>
      </Col>
    ))}
  </Row>
));

const TableSkeleton = React.memo(() => (
  <Card className="contacts-card">
    <div style={{ marginBottom: 16 }}>
      <div style={{ height: 32, background: SKELETON_GRAD, borderRadius: 6, width: "40%", marginBottom: 12, animation: SKEL_ANIM }} />
      <div style={{ height: 32, background: SKELETON_GRAD, borderRadius: 6, width: "60%", animation: SKEL_ANIM }} />
    </div>
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}>
      <div style={{ padding: "12px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0", display: "flex", gap: 16 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ height: 20, background: SKELETON_GRAD_DARK, borderRadius: 4, flex: i === 0 ? "0 0 60px" : "1", animation: SKEL_ANIM }} />
        ))}
      </div>
      {[0, 1, 2, 3, 4].map((r) => (
        <div key={r} style={{ padding: "12px 16px", borderBottom: r < 4 ? "1px solid #f0f0f0" : "none", display: "flex", gap: 16, alignItems: "center" }}>
          {[0, 1, 2, 3, 4, 5].map((c) => (
            <div key={c} style={{ height: c === 0 ? 24 : c % 2 === 0 ? 16 : 20, background: SKELETON_GRAD, borderRadius: 4, flex: c === 0 ? "0 0 60px" : "1", width: c === 0 ? "60px" : c % 3 === 0 ? "70%" : "100%", animation: SKEL_ANIM }} />
          ))}
        </div>
      ))}
    </div>
    <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div style={{ height: 24, background: SKELETON_GRAD, borderRadius: 4, width: 120, animation: SKEL_ANIM }} />
      <div style={{ height: 32, background: SKELETON_GRAD, borderRadius: 6, width: 200, animation: SKEL_ANIM }} />
    </div>
  </Card>
));

const AddressBookSkeleton = React.memo(() => (
  <div style={{ height: 32, background: SKELETON_GRAD, borderRadius: 6, width: "100%", minWidth: 200, animation: SKEL_ANIM }} />
));

const ContactsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const { updateContactsStats } = useContacts();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [addContactVisible, setAddContactVisible] = useState(false);
  const [editContactVisible, setEditContactVisible] = useState(false);
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [headerMapping, setHeaderMapping] = useState({});
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [mappingStep, setMappingStep] = useState(1); // 1: Upload, 2: Map Headers, 3: Review
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [advancedFilterVisible, setAdvancedFilterVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Higher default for Excel view
  const [totalContacts, setTotalContacts] = useState(0);
  const [activeFilters, setActiveFilters] = useState({
    search: "",
    mobileno: "",
    labelfilter: [], // Changed to array to support multiple labels
  });
  const [categoryStats, setCategoryStats] = useState({
    general: 0,
    premium: 0,
    vip: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // State and City management
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // Phone number management for Add/Edit forms
  const [addPhoneNumber, setAddPhoneNumber] = useState("");
  const [addCountryCode, setAddCountryCode] = useState("+91");
  const [editPhoneNumber, setEditPhoneNumber] = useState("");
  const [editCountryCode, setEditCountryCode] = useState("+91");

  // Debouncing for search
  const searchTimeoutRef = useRef(null);
  
  // Rate limiting for refresh button
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const lastRefreshRef = useRef(0);
  const refreshInProgressRef = useRef(false);
  const REFRESH_COOLDOWN_MS = 3000; // 3 second cooldown

  // Ref to always hold the latest currentAddressBook - fixes stale closure in handleRefresh
  const _currentAddressBookRef = useRef(null);

  // Dynamic table support
  const [hasContacts, setHasContacts] = useState(false);
  const [columns, setColumns] = useState([]);
  const [userColumnsMeta, setUserColumnsMeta] = useState([]); // raw API column metadata
  const [editContactFields, setEditContactFields] = useState([]); // dynamic fields for edit contact

  // Additional Info modal
  // additionalInfoContact state removed — extra info is now shown inline in the table column

  // Form instances
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filterForm] = Form.useForm();

  // Address Book Management States
  const [addressBooks, setAddressBooks] = useState([]);
  const [currentAddressBook, setCurrentAddressBook] = useState(null);
  const [showAddressBookModal, setShowAddressBookModal] = useState(false);
  const [addressBookLoading, setAddressBookLoading] = useState(true); // true on mount — we always fetch books immediately
  const [newAddressBookName, setNewAddressBookName] = useState("");

  // Label Management States
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [removeLabelLoading, setRemoveLabelLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [customLabelName, setCustomLabelName] = useState("");
  const [labelForm] = Form.useForm();
  const [availableLabels, setAvailableLabels] = useState([]); // All labels from contacts

  // Mobile number validator
  const mobileValidator = (value) => {
    if (!value) {
      return Promise.resolve();
    }
    // if (!/^([6|7|8|9]{1})[0-9]{9}$/.test(value)) {
    //   return Promise.reject("Mobile number is invalid!");
    // }
    // if (value.length !== 10) {
    //   return Promise.reject("Mobile number must be 10 digits!");
    // }
    return Promise.resolve();
  };

  // Predefined labels with colors and icons
  const predefinedLabels = [
    {
      value: "favourite",
      label: "Favourite",
      color: "default",
      icon: <HeartOutlined />,
      description: "Mark as favourite contact",
    },
    {
      value: "close",
      label: "Close Friend",
      color: "default",
      icon: <StarOutlined />,
      description: "Close friend or important contact",
    },
    {
      value: "family",
      label: "Family",
      color: "default",
      icon: <HomeOutlined />,
      description: "Family member",
    },
    {
      value: "business",
      label: "Business",
      color: "default",
      icon: <TagOutlined />,
      description: "Business contact",
    },
    {
      value: "vip",
      label: "VIP Customer",
      color: "default",
      icon: <StarOutlined />,
      description: "VIP or premium customer",
    },
  ];

  // Build AntD columns from contact data dynamically
  const buildColumnsFromContactData = (contactsData = []) => {
    if (contactsData.length === 0) {
      return buildDefaultColumns();
    }

    const hiddenFields = new Set(["userid", "companyid", "entrytime"]);

    // Get all unique keys from the first contact to build columns
    const sampleContact = contactsData[0];
    const allKeys = Object.keys(sampleContact);

    // Separate standard fields from userdata fields
    const standardFields = [
      "username",
      "mobileno",
      "emailid",
      "countrycode",
      "address",
    ];
    const userDataFields = allKeys.filter((key) => key.startsWith("userdata_"));

    const baseCols = [];

    // Add standard columns first
    standardFields.forEach((fieldName) => {
      if (
        !hiddenFields.has(fieldName) &&
        sampleContact.hasOwnProperty(fieldName)
      ) {
        const title = fieldName
          .replace(/_/g, " ")
          .replace(/\b\w/g, (m) => m.toUpperCase());

        if (fieldName === "username") {
          baseCols.push({
            title: "Name",
            dataIndex: fieldName,
            key: fieldName,
            width: 200,
            ellipsis: true,
            render: (val, record) => (
              <div className="cp-table-name-cell">
                <Avatar className="cp-table-avatar" icon={<UserOutlined />} />
                <div>
                  <div className="cp-table-name-text">{val}</div>
                  {record.usercategory && (
                    <div className="cp-table-name-sub">{record.usercategory}</div>
                  )}
                </div>
              </div>
            ),
          });

          // Add separate Labels column after Name
          baseCols.push({
            title: "Labels",
            key: "labels",
            width: 200,
            ellipsis: true,
            render: (_, record) => {
              // Handle both single label (string) and multiple labels (array or JSON string)
              let labelsArray = [];

              // Check if record.labels exists (array format)
              if (record.labels) {
                labelsArray = Array.isArray(record.labels)
                  ? record.labels
                  : [record.labels];
              }
              // Check if record.label exists (could be JSON string or single value)
              else if (record.label) {
                try {
                  // Try to parse as JSON string first
                  if (
                    typeof record.label === "string" &&
                    record.label.startsWith("[")
                  ) {
                    labelsArray = JSON.parse(record.label);
                  } else {
                    // Single label value
                    labelsArray = [record.label];
                  }
                } catch (error) {
                  // If JSON parsing fails, treat as single label
                  labelsArray = [record.label];
                }
              }

              // Filter out any empty or null values
              labelsArray = labelsArray.filter(Boolean);

              if (labelsArray.length === 0) {
                return (
                  <span className="cp-table-no-labels">No labels</span>
                );
              }

              return (
                <div className="cp-table-labels-wrap">
                  {labelsArray.map((labelValue, index) => {
                    // Find the matching predefined label
                    const labelInfo = predefinedLabels.find(
                      (l) => l.value === labelValue,
                    );

                    if (labelInfo) {
                      return (
                        <Tag
                          key={index}
                          color={labelInfo.color}
                          size="small"
                          className={`cp-table-label cp-table-label--${labelValue}`}
                        >
                          <span className="cp-table-label-icon">
                            {labelInfo.icon}
                          </span>
                          {labelInfo.label}
                        </Tag>
                      );
                    } else {
                      // Custom label
                      return (
                        <Tag
                          key={index}
                          color="default"
                          size="small"
                          className="cp-table-label cp-table-label--custom"
                        >
                          <TagOutlined className="cp-table-label-icon" />
                          {labelValue}
                        </Tag>
                      );
                    }
                  })}
                </div>
              );
            },
          });
        } else if (fieldName === "mobileno") {
          baseCols.push({
            title: "Mobile",
            dataIndex: fieldName,
            key: fieldName,
            width: 150,
            ellipsis: true,
            render: (mobile, record) => (
              <div className="cp-table-phone-cell">
                <PhoneOutlined className="cp-table-phone-icon" />
                <span className="cp-table-country-code">+{record.countrycode || '--'}</span>
                <span className="cp-table-phone-number">{mobile || '--'}</span>
              </div>
            ),
          });
        } else if (fieldName === "emailid") {
          baseCols.push({
            title: "Email",
            dataIndex: fieldName,
            key: fieldName,
            width: 200,
            ellipsis: true,
            render: (email) => (
              <div className="cp-table-email-cell">
                <MailOutlined className="cp-table-email-icon" />
                {email}
              </div>
            ),
          });
        } else if (fieldName === "address") {
          baseCols.push({
            title: "Address",
            dataIndex: fieldName,
            key: fieldName,
            width: 200,
            ellipsis: true,
          });
        }
      }
    });

    // Add userdata columns
    userDataFields.forEach((fieldName) => {
      const displayName = fieldName
        .replace("userdata_", "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());
      baseCols.push({
        title: displayName,
        dataIndex: fieldName,
        key: fieldName,
        width: 150,
        ellipsis: true,
        render: (val) => val || "-",
      });
    });

    return buildFinalColumns(baseCols);
  };

  // Build default columns when no data is available
  const buildDefaultColumns = () => {
    const defaultCols = [
      {
        title: "Name",
        dataIndex: "username",
        key: "username",
        width: 200,
        ellipsis: true,
        render: (val, record) => (
          <div className="cp-table-name-cell">
            <Avatar className="cp-table-avatar" icon={<UserOutlined />} />
            <div>
              <div className="cp-table-name-text">{val}</div>
              {record.usercategory ? (
                <div className="cp-table-name-sub">{record.usercategory}</div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        title: "Mobile",
        dataIndex: "mobileno",
        key: "mobileno",
        width: 150,
        ellipsis: true,
        render: (mobile, record) => (
          <div className="cp-table-phone-cell">
            <PhoneOutlined className="cp-table-phone-icon" />
            <span className="cp-table-country-code">+{record.countrycode || '--'}</span>
            <span className="cp-table-phone-number">{mobile || '--'}</span>
          </div>
        ),
      },
      {
        title: "Email",
        dataIndex: "emailid",
        key: "emailid",
        width: 200,
        ellipsis: true,
        render: (email) => (
          <div className="cp-table-email-cell">
            <MailOutlined className="cp-table-email-icon" />
            {email}
          </div>
        ),
      },
      {
        title: "Address",
        dataIndex: "address",
        key: "address",
        width: 200,
        ellipsis: true,
      },
    ];

    return buildFinalColumns(defaultCols);
  };

  // Add Sr No and Actions columns to any column set
  const buildFinalColumns = (baseCols) => {
    // Prepend Sr No column with dynamic calculation based on current state
    const cols = [
      {
        title: "Sr No",
        key: "__srno",
        width: 80,
        render: (_val, _record, index) => {
          const serialNumber = Math.max(
            1,
            (currentPage - 1) * pageSize + index + 1,
          );
          return <span className="cp-table-srno">{serialNumber}</span>;
        },
      },
      ...baseCols,
    ];
    // Append actions column at the end
    cols.push({
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const isLastContact = totalContacts <= 1;

        const menu = (
          <Menu>
            <Menu.Item
              key="edit"
              icon={<EditOutlined />}
              onClick={() => handleEditContact(record)}
            >
              Update
            </Menu.Item>
            <Menu.Item key="message" icon={<MessageOutlined />}>
              Send Message
            </Menu.Item>
            <Menu.Divider />
            {isLastContact ? (
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                disabled
                style={{ color: "#cbd5e1", cursor: "not-allowed" }}
              >
                Delete (Last Contact)
              </Menu.Item>
            ) : (
              <Popconfirm
                title="Delete Contact"
                description="Are you sure you want to delete this contact?"
                onConfirm={() => handleDeleteContact(record)}
                okText="Yes"
                cancelText="No"
              >
                <Menu.Item key="delete" icon={<DeleteOutlined />} danger>
                  Delete
                </Menu.Item>
              </Popconfirm>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} className="cp-table-actions-btn" />
          </Dropdown>
        );
      },
    });

    return cols;
  };

  // Define mandatory/required fields (using actual database column names)
  // Only username, mobileno, countrycode, and emailid are truly required for import
  // State, city, usercategory, and status are optional - not required in addressbook
  const requiredFields = new Set([
    "username",
    "mobileno",
    "emailid",
    // Commented out optional fields - not required in addressbook:
    // 'address', 'status', 'usercategory', 'stateid', 'cityid'
  ]);

  // Helpers for dynamic form
  const toLabel = (name = "") =>
    (name || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const hiddenForForm = new Set(["userid", "companyid", "entrytime"]);

  const renderDynamicFormItem = (col) => {
    const name = col.columnName;
    // Use displayName if available (for userdata fields), otherwise convert columnName to label
    const label = col.displayName
      ? toLabel(col.displayName)
      : toLabel(name.replace("userdata_", ""));
    const isRequired = requiredFields.has(name);
    const rules = isRequired
      ? [{ required: true, message: `${label} is required` }]
      : [];

    // Special field handlers with proper required validation
    if (name === "mobileno") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Mobile Number"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <PhoneInput
            countryCodeEditable={false}
            country={"in"}
            value={addPhoneNumber}
            onChange={handleAddPhoneChange}
            inputClass="contacts-phone-input"
            containerClass="contacts-phone-container"
            dropdownClass="contacts-phone-dropdown"
            buttonClass="contacts-phone-flag-button"
            enableSearch={true}
            disableSearchIcon={true}
            searchPlaceholder="Search country..."
            placeholder="Enter mobile number"
            inputStyle={{
              width: "100%",
              height: "32px",
              fontSize: "14px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              paddingLeft: "48px",
              transition: "all 0.3s ease",
            }}
            buttonStyle={{
              border: "1px solid #e2e8f0",
              borderRight: "none",
              borderRadius: "6px 0 0 6px",
              background: "#f8fafc",
              transition: "all 0.3s ease",
            }}
          />
        </Form.Item>
      );
    }
    // Countrycode input (readonly)
    if (name === "countrycode") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Country Code"
          rules={[]} // not required
          style={{ visibility: "hidden", height: 0, margin: 0, padding: 0 }}
        >
          <Input
            countryCodeEditable = {false}
            placeholder="Country Code"
            readOnly
            style={{ visibility: "hidden", height: 0, margin: 0, padding: 0 }}
          />
        </Form.Item>
      );
    }

    if (name === "stateid") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="State"
          rules={
            isRequired ? [{ required: true, message: `State is required` }] : []
          }
        >
          <Select
            placeholder="Select state"
            onChange={handleAddStateChange}
            loading={statesLoading}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {statesList.map((state) => (
              <Option key={state.stateid} value={state.stateid}>
                {state.statename}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (name === "cityid") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="City"
          rules={
            isRequired ? [{ required: true, message: `City is required` }] : []
          }
        >
          <Select
            placeholder="Select city"
            loading={citiesLoading}
            disabled={citiesList.length === 0}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {citiesList.map((city) => (
              <Option key={city.cityid} value={city.cityid}>
                {city.cityname}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (name === "emailid") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Email"
          rules={
            isRequired
              ? [
                  { required: true, message: `${label} is required` },
                  { type: "email", message: "Please enter valid email" },
                ]
              : [{ type: "email", message: "Please enter valid email" }]
          }
        >
          <Input placeholder="Enter email address" />
        </Form.Item>
      );
    }

    if (name === "username") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="First Name"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <Input placeholder="Enter first name" />
        </Form.Item>
      );
    }

    if (name === "address") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Address"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <Input.TextArea placeholder="Enter address" rows={3} />
        </Form.Item>
      );
    }

    if (name === "status") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Status"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
          initialValue="active"
        >
          <Select placeholder="Select status">
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="pending">Pending</Option>
          </Select>
        </Form.Item>
      );
    }

    if (name === "usercategory") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="User Category"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <Select placeholder="Select category">
            <Option value="general">General</Option>
            <Option value="premium">Premium</Option>
            <Option value="vip">VIP</Option>
          </Select>
        </Form.Item>
      );
    }

    // Type-based components with conditional required validation
    const dt = (col.dataType || "").toLowerCase();
    if (dt.includes("date") && !dt.includes("time")) {
      // DATE
      return (
        <Form.Item key={name} name={name} label={label} rules={rules}>
          <Input placeholder={`Enter ${label}`} />
        </Form.Item>
      );
    }
    if (
      dt.includes("timestamp") ||
      (dt.includes("time") && dt.includes("zone"))
    ) {
      return (
        <Form.Item key={name} name={name} label={label} rules={rules}>
          <Input placeholder={`Enter ${label}`} />
        </Form.Item>
      );
    }
    if (
      dt.includes("numeric") ||
      dt.includes("integer") ||
      dt.includes("smallint") ||
      dt.includes("decimal")
    ) {
      return (
        <Form.Item key={name} name={name} label={label} rules={rules}>
          <Input placeholder={`Enter ${label}`} />
        </Form.Item>
      );
    }

    // Default text input for all other fields
    return (
      <Form.Item key={name} name={name} label={label} rules={rules}>
        <Input placeholder={`Enter ${label}`} />
      </Form.Item>
    );
  };

  // Ensure all required fields are present in the form
  // Only truly required fields: username, mobileno, emailid
  const ensureRequiredFields = (columns) => {
    const existingFields = new Set(columns.map((col) => col.columnName));
    const missingRequired = [];

    requiredFields.forEach((field) => {
      if (!existingFields.has(field)) {
        // Create a mock column structure for missing required fields
        let dataType = "character varying";
        if (field === "mobileno") dataType = "character varying(20)";
        if (field === "emailid") dataType = "character varying(255)";
        // Removed optional fields: address, status, usercategory, stateid, cityid

        missingRequired.push({
          columnName: field,
          dataType: dataType,
          isNullable: "NO",
          columnDefault: null,
        });
      }
    });

    return [...columns, ...missingRequired];
  };

  // Deduplicate form fields - prefer ID fields over name fields
  const deduplicateFormFields = (columns) => {
    const seen = new Set();
    const fieldPriority = {
      stateid: "state",
      cityid: "city",
    };

    return columns.filter((col) => {
      const name = col.columnName;

      // If this is a priority field (ID), mark its alternative as seen
      if (fieldPriority[name]) {
        seen.add(fieldPriority[name]);
        seen.add(name);
        return true;
      }

      // If this field's priority version was already seen, skip it
      if (seen.has(name)) {
        return false;
      }

      seen.add(name);
      return true;
    });
  };

  // Normalize contact data to ensure proper types
  const normalizeContactData = (contacts) => {
    return contacts.map((contact) => ({
      ...contact,
      status: contact.status || "active", // Default to 'active' if status is missing
      usercategory: contact.usercategory || "general", // Default to 'general' if category is missing
      username: contact.username || "",
      emailid: contact.emailid || "",
      mobileno: contact.mobileno || "",
      address: contact.address || "",
      state: contact.state || "",
      city: contact.city || "",
      stateid: contact.stateid || null,
      cityid: contact.cityid || null,
      countrycode: contact.countrycode || "+91",
    }));
  };

  // Normalize contact data with userdata field for new API structure
  // Only include fields that actually exist in the API response
  const normalizeContactDataWithUserData = (contacts) => {
    return contacts.map((contact) => {
      const normalizedContact = {
        ...contact,
        // Only set defaults for truly required fields
        username: contact.username || "",
        emailid: contact.emailid || "",
        mobileno: contact.mobileno || "",
        countrycode: contact.countrycode || "91",
      };

      // Only include address if it exists in the contact
      if (contact.address !== undefined) {
        normalizedContact.address = contact.address || "";
      }

      // Keep label if it exists (for display purposes, not a form field)
      if (contact.label !== undefined) {
        normalizedContact.label = contact.label;
      }

      // Merge userdata fields into the main contact object for easier access
      if (contact.userdata && typeof contact.userdata === "object") {
        Object.keys(contact.userdata).forEach((key) => {
          normalizedContact[`userdata_${key}`] = contact.userdata[key];
        });
      }

      return normalizedContact;
    });
  };

  // Function to extract all unique labels from contacts
  const extractLabelsFromContacts = (contacts) => {
    const labelsSet = new Set();

    contacts.forEach((contact) => {
      let labels = [];

      // Check different possible label fields
      if (contact.label) {
        if (typeof contact.label === "string") {
          try {
            labels = JSON.parse(contact.label);
          } catch {
            labels = [contact.label];
          }
        } else if (Array.isArray(contact.label)) {
          labels = contact.label;
        }
      } else if (contact.labels) {
        if (typeof contact.labels === "string") {
          try {
            labels = JSON.parse(contact.labels);
          } catch {
            labels = [contact.labels];
          }
        } else if (Array.isArray(contact.labels)) {
          labels = contact.labels;
        }
      }

      // Add each label to the set
      labels.forEach((label) => {
        if (label && label.trim()) {
          labelsSet.add(label.trim());
        }
      });
    });

    return Array.from(labelsSet).sort();
  };

  // Fetch all labels from address book using dedicated labels API
  const fetchAllLabelsFromAddressBook = async (bookid = null) => {
    const targetBookId = bookid || currentAddressBook?.bookid;
    if (!targetBookId) {
      setAvailableLabels([]);
      return;
    }

    try {
      const response = await axiosConfig.post("/addressbook/labels", {
        bookid: targetBookId,
      });

      if (
        (response.data?.status === true || response.data?.statuscode === 200) &&
        response.data?.entity?.labels
      ) {
        const labels = response.data.entity.labels;
        setAvailableLabels(Array.isArray(labels) ? labels.sort() : []);
      } else {
        setAvailableLabels([]);
      }
    } catch (error) {
      console.error("Error fetching labels from API:", error);
      // Fallback: extract labels from currently loaded contacts
      if (contacts.length > 0) {
        const fallbackLabels = extractLabelsFromContacts(contacts);
        setAvailableLabels(fallbackLabels);
      } else {
        setAvailableLabels([]);
      }
    }
  };

  // Build columns dynamically from normalized API response data
  // userdataColumns: optional array of column names from API (e.g. ["amount", "designation", "gender"])
  const buildColumnsFromNormalizedData = (normalizedData, userdataColumns) => {
    if (normalizedData.length === 0 && (!userdataColumns || userdataColumns.length === 0)) {
      return buildDefaultColumns();
    }

    const sampleContact = normalizedData[0] || {};
    const columns = [];

    // Always include standard fields first
    const standardFields = [
      { key: "username", title: "Name", width: 200 },
      { key: "mobileno", title: "Mobile", width: 150 },
      { key: "emailid", title: "Email", width: 200 },
      { key: "countrycode", title: "Country Code", width: 120 },
      { key: "address", title: "Address", width: 250 },
    ];

    // Add standard columns
    standardFields.forEach((field) => {
      if (sampleContact.hasOwnProperty(field.key)) {
        if (field.key === "username") {
          columns.push({
            title: field.title,
            dataIndex: field.key,
            key: field.key,
            width: field.width,
            ellipsis: true,
            render: (val, record) => (
              <div className="cp-table-name-cell">
                <Avatar className="cp-table-avatar" icon={<UserOutlined />} />
                <div>
                  <div className="cp-table-name-text">{val || "N/A"}</div>
                  {record.userdata_category && (
                    <div className="cp-table-name-sub">{record.userdata_category}</div>
                  )}
                </div>
              </div>
            ),
          });
        } else {
          columns.push({
            title: field.title,
            dataIndex: field.key,
            key: field.key,
            width: field.width,
            ellipsis: true,
            render: (val) => val || "N/A",
          });
        }
      }
    });

    // Use userdataColumns from API if available, otherwise fall back to scanning first contact
    let userDataFields;
    if (Array.isArray(userdataColumns) && userdataColumns.length > 0) {
      // API provides authoritative column list — prefix each with "userdata_"
      userDataFields = userdataColumns.map((col) => `userdata_${col}`);
    } else {
      // Fallback: derive from first contact's keys
      userDataFields = Object.keys(sampleContact)
        .filter((key) => key.startsWith("userdata_"))
        .sort();
    }

    // Add dynamic userdata columns
    userDataFields.forEach((fieldKey) => {
      const fieldName = fieldKey.replace("userdata_", "");
      const title = fieldName
        .replace(/[._]/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());

      columns.push({
        title: title,
        dataIndex: fieldKey,
        key: fieldKey,
        width: 150,
        ellipsis: true,
        render: (val) => val || "N/A",
      });
    });

    // Add actions column
    columns.push({
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => {
        const isLastContact = totalContacts <= 1;

        return (
          <Space size="small">
            <Tooltip title="Edit Contact">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEditContact(record)}
                className="cp-table-actions-btn"
              />
            </Tooltip>
            <Tooltip
              title={
                isLastContact ? "Cannot delete last contact" : "Delete Contact"
              }
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteContact(record)}
                disabled={isLastContact}
                style={{
                  color: isLastContact ? "#cbd5e1" : "var(--cp-danger)",
                  cursor: isLastContact ? "not-allowed" : "pointer",
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    });

    return columns;
  };

  // Build columns based on current contacts data
  const buildColumnsFromCurrentData = () => {
    if (contacts.length > 0) {
      const cols = buildColumnsFromContactData(contacts);
      setColumns(cols);
    } else {
      const cols = buildDefaultColumns();
      setColumns(cols);
    }
  };

  // Build userColumnsMeta from actual contact data
  // Only include fields that actually exist in the addressbook contacts
  // userdataColumns: optional array of column names from API (e.g. ["amount", "designation", "gender"])
  const buildUserColumnsMeta = (contactsData, userdataColumns) => {
    if ((!contactsData || contactsData.length === 0) && (!userdataColumns || userdataColumns.length === 0)) {
      return [];
    }

    const sampleContact = (contactsData && contactsData[0]) || {};
    const columnsMeta = [];

    // Fields to exclude from forms (system fields and non-column fields)
    // userdata - container object, not a column
    // label - feature field, not an input column
    // usercategory - not in API response
    // status - not in API response
    const excludeFields = new Set([
      "userid",
      "companyid",
      "entrytime",
      "addressbookid",
      "status",
      "usercategory",
      "label",
      "userdata",
    ]);

    // Define the order of standard fields
    const standardFieldOrder = [
      "username",
      "mobileno",
      "emailid",
      "countrycode",
      "address",
    ];

    // First add standard fields in order (if they exist)
    standardFieldOrder.forEach((fieldName) => {
      if (
        sampleContact.hasOwnProperty(fieldName) &&
        !excludeFields.has(fieldName)
      ) {
        columnsMeta.push({
          columnName: fieldName,
          dataType: fieldName === "address" ? "text" : "character varying",
          isNullable: fieldName === "address" ? "YES" : "NO",
        });
      }
    });

    // Then add userdata fields (custom columns from uploaded file)
    // Use userdataColumns from API if available, otherwise fall back to scanning first contact
    let userDataKeys;
    if (Array.isArray(userdataColumns) && userdataColumns.length > 0) {
      userDataKeys = userdataColumns.map((col) => `userdata_${col}`);
    } else {
      userDataKeys = Object.keys(sampleContact).filter((key) =>
        key.startsWith("userdata_")
      );
    }

    userDataKeys.forEach((key) => {
      const fieldName = key.replace("userdata_", "");
      columnsMeta.push({
        columnName: key, // Keep the userdata_ prefix for form field name
        displayName: fieldName, // Use clean name for label
        dataType: "character varying",
        isNullable: "YES",
        isUserData: true,
      });
    });

    return columnsMeta;
  };

  // Render dynamic form item for edit form (different from add form)
  const renderEditFormItem = (col) => {
    const name = col.columnName;
    const label = toLabel(name);
    const isRequired = requiredFields.has(name);
    const rules = isRequired
      ? [{ required: true, message: `${label} is required` }]
      : [];

    // Special field handlers for edit form
    if (name === "mobileno") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Mobile Number"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <PhoneInput
            country={"in"}
            value={editPhoneNumber}
            onChange={handleEditPhoneChange}
            inputClass="contacts-phone-input"
            containerClass="contacts-phone-container"
            dropdownClass="contacts-phone-dropdown"
            buttonClass="contacts-phone-flag-button"
            enableSearch={true}
            disableSearchIcon={true}
            searchPlaceholder="Search country..."
            placeholder="Enter mobile number"
            inputStyle={{
              width: "100%",
              height: "32px",
              fontSize: "14px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              paddingLeft: "48px",
              transition: "all 0.3s ease",
            }}
            buttonStyle={{
              border: "1px solid #e2e8f0",
              borderRight: "none",
              borderRadius: "6px 0 0 6px",
              background: "#f8fafc",
              transition: "all 0.3s ease",
            }}
          />
        </Form.Item>
      );
    }

    if (name === "stateid") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="State"
          rules={
            isRequired ? [{ required: true, message: `State is required` }] : []
          }
        >
          <Select
            placeholder="Select state"
            onChange={handleEditStateChange}
            loading={statesLoading}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {statesList.map((state) => (
              <Option key={state.stateid} value={state.stateid}>
                {state.statename}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (name === "cityid") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="City"
          rules={
            isRequired ? [{ required: true, message: `City is required` }] : []
          }
        >
          <Select
            placeholder="Select city"
            loading={citiesLoading}
            disabled={citiesList.length === 0}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {citiesList.map((city) => (
              <Option key={city.cityid} value={city.cityid}>
                {city.cityname}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    }

    if (name === "emailid") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Email"
          rules={
            isRequired
              ? [
                  { required: true, message: `${label} is required` },
                  { type: "email", message: "Please enter valid email" },
                ]
              : [{ type: "email", message: "Please enter valid email" }]
          }
        >
          <Input placeholder="Enter email address" />
        </Form.Item>
      );
    }

    if (name === "username") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="First Name"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <Input placeholder="Enter first name" />
        </Form.Item>
      );
    }

    if (name === "address") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="Address"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <Input.TextArea placeholder="Enter address" rows={3} />
        </Form.Item>
      );
    }

    if (name === "usercategory") {
      return (
        <Form.Item
          key={name}
          name={name}
          label="User Category"
          rules={
            isRequired
              ? [{ required: true, message: `${label} is required` }]
              : []
          }
        >
          <Select placeholder="Select category">
            <Option value="general">General</Option>
            <Option value="premium">Premium</Option>
            <Option value="vip">VIP</Option>
            <Option value="customer">Customer</Option>
            <Option value="supplier">Supplier</Option>
          </Select>
        </Form.Item>
      );
    }

    // Default text input for userdata and other fields
    return (
      <Form.Item key={name} name={name} label={label} rules={rules}>
        <Input placeholder={`Enter ${label}`} />
      </Form.Item>
    );
  };

  // Dynamic Edit Form Item Renderer - Based on Actual Contact Data
  const renderDynamicEditFormItem = (field) => {
    const { columnName, label, type, required, isUserData } = field;
    const rules = required
      ? [{ required: true, message: `${label} is required` }]
      : [];

    // Handle phone number field
    if (columnName === "mobileno") {
      return (
        <Form.Item
          key={columnName}
          name={columnName}
          label={label}
          rules={rules}
        >
          <PhoneInput
            country={"in"}
            value={editPhoneNumber}
            onChange={handleEditPhoneChange}
            inputClass="contacts-phone-input"
            containerClass="contacts-phone-container"
            dropdownClass="contacts-phone-dropdown"
            buttonClass="contacts-phone-flag-button"
            enableSearch={true}
            disableSearchIcon={true}
            searchPlaceholder="Search country..."
            placeholder="Enter mobile number"
            inputStyle={{
              width: "100%",
              height: "32px",
              fontSize: "14px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              paddingLeft: "48px",
              transition: "all 0.3s ease",
            }}
            buttonStyle={{
              border: "1px solid #e2e8f0",
              borderRight: "none",
              borderRadius: "6px 0 0 6px",
              background: "#f8fafc",
              transition: "all 0.3s ease",
            }}
          />
        </Form.Item>
      );
    }

    // Handle email field
    if (columnName === "emailid" || type === "email") {
      return (
        <Form.Item
          key={columnName}
          name={columnName}
          label={label}
          rules={
            required
              ? [
                  { required: true, message: `${label} is required` },
                  { type: "email", message: "Please enter valid email" },
                ]
              : [{ type: "email", message: "Please enter valid email" }]
          }
        >
          <Input placeholder={`Enter ${label.toLowerCase()}`} />
        </Form.Item>
      );
    }

    // Handle country code field
    if (columnName === "countrycode" || type === "countrycode") {
      return (
        <Form.Item
          key={columnName}
          name={columnName}
          label={label}
          rules={[]} // not required
          style={{ visibility: "hidden", height: 0, margin: 0, padding: 0 }}
        >
          <Input
            placeholder="Country Code"
            readOnly
            style={{ visibility: "hidden", height: 0, margin: 0, padding: 0 }}
          />
        </Form.Item>
      );
    }

    // Handle textarea for address
    if (columnName === "address" || type === "textarea") {
      return (
        <Form.Item
          key={columnName}
          name={columnName}
          label={label}
          rules={rules}
        >
          <Input.TextArea
            placeholder={`Enter ${label.toLowerCase()}`}
            rows={3}
          />
        </Form.Item>
      );
    }

    // Hide Sr.No input field
    if (columnName === "srno" || type === "srno") {
      return (
        <Form.Item
          key={columnName}
          name={columnName}
          style={{ visibility: "hidden", height: 0, margin: 0, padding: 0 }}
        >
          <Input
            style={{ visibility: "hidden", height: 0, margin: 0, padding: 0 }}
          />
        </Form.Item>
      );
    }

    // Handle hidden fields (like country code)
    if (type === "hidden") {
      return (
        <Form.Item key={columnName} name={columnName} hidden>
          <Input />
        </Form.Item>
      );
    }

    // Handle userdata fields with special styling
    if (isUserData) {
      return (
        <Form.Item
          key={columnName}
          name={columnName}
          label={
            <span>
              {label}
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                (Custom Field)
              </Text>
            </span>
          }
          rules={rules}
        >
          <Input
            placeholder={`Enter ${label.toLowerCase()}`}
            style={{
              borderColor: "#22c55e",
              backgroundColor: "rgba(34, 197, 94, 0.02)",
            }}
          />
        </Form.Item>
      );
    }

    // Default input field
    return (
      <Form.Item key={columnName} name={columnName} label={label} rules={rules}>
        <Input placeholder={`Enter ${label.toLowerCase()}`} />
      </Form.Item>
    );
  };

  // Address Book Management Functions
  const fetchAddressBooks = async () => {
    try {
      setAddressBookLoading(true);
      const response = await axiosConfig.get("/addressbook/details");
      if (response.data?.statuscode === 200 && response.data?.entity) {
        const books = response.data.entity;
        setAddressBooks(books);

        // Set first address book as current if none selected
        if (books.length > 0 && !currentAddressBook) {
          setCurrentAddressBook(books[0]);
        }
      } else {
        setAddressBooks([]);
        setCurrentAddressBook(null);
        // Clear loading states when no address books exist
        setHasContacts(false);
        setTableLoading(false);
        setContacts([]);
        setFilteredContacts([]);
      }
    } catch (error) {
      console.error("Error fetching address books:", error);
      setAddressBooks([]);
      setCurrentAddressBook(null);
      // Clear loading states on error as well
      setHasContacts(false);
      setTableLoading(false);
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setAddressBookLoading(false);
    }
  };

  // Address book will be created automatically during upload
  // No separate create function needed

  const deleteAddressBook = async (addressBookId) => {
    console.log("Deleting address book with ID:", addressBookId);

    if (!addressBookId) {
      message.error("Address book ID is required");
      return;
    }

    try {
      setAddressBookLoading(true);
      const response = await axiosConfig.post("/addressbook/delete", {
        bookid: addressBookId,
      });

      if (response.data?.statuscode === 200) {
        // message.success('Address book deleted successfully!');

        // If deleted book was current, switch to first available
        if (currentAddressBook?.bookid === addressBookId) {
          const remaining = addressBooks.filter(
            (book) => book.bookid !== addressBookId,
          );
          setCurrentAddressBook(remaining.length > 0 ? remaining[0] : null);
        }

        fetchAddressBooks(); // Refresh the list
      } else {
        message.error(
          response.data?.message || "Failed to delete address book",
        );
      }
    } catch (error) {
      console.error("Error deleting address book:", error);
      message.error("Failed to delete address book");
    } finally {
      setAddressBookLoading(false);
    }
  };

  const switchAddressBook = (addressBook) => {
    setCurrentAddressBook(addressBook);
    setContacts([]);
    setFilteredContacts([]);
    setHasContacts(false);
    // fetchContacts will be called by useEffect when currentAddressBook changes
  };

  // API Functions for States and Cities
  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const response = await axiosConfig.post("/statelist", {});

      if (response.data?.statuscode === 200) {
        setStatesList(response.data?.entity?.statelist || []);
      } else {
        // message.error("Failed to fetch states");
        setStatesList([]);
      }
    } catch (error) {
      console.error("Fetch states error:", error);
      // message.error("Failed to fetch states");
      setStatesList([]);
    } finally {
      setStatesLoading(false);
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCitiesList([]);
      return Promise.resolve();
    }

    try {
      setCitiesLoading(true);
      const response = await axiosConfig.post("/citylist", {
        stateId: stateId,
      });

      if (response.data?.statuscode === 200) {
        setCitiesList(response.data?.entity?.citylist || []);
        return Promise.resolve();
      } else {
        message.error("Failed to fetch cities");
        setCitiesList([]);
        return Promise.reject();
      }
    } catch (error) {
      console.error("Fetch cities error:", error);
      message.error("Failed to fetch cities");
      setCitiesList([]);
      return Promise.reject();
    } finally {
      setCitiesLoading(false);
    }
  };

  // Phone number handlers - Using SignupPage logic
  const handleAddPhoneChange = (value, country) => {
    const dialCode = country.dialCode;
    setAddPhoneNumber(value);
    const code = `+${country.dialCode}`;
    setAddCountryCode(code);

    // Extract local number without country code - SignupPage method
    const localNumber = value.replace(code, "");

    addForm.setFieldsValue({
      mobileno: localNumber,
      countrycode: code,
    });
  };

  const handleEditPhoneChange = (value, country) => {
    const dialCode = country.dialCode;
    setEditPhoneNumber(value);
    const code = `+${country.dialCode}`;
    setEditCountryCode(code);

    // Extract local number without country code - SignupPage method
    const localNumber = value.replace(code, "");

    editForm.setFieldsValue({
      mobileno: localNumber,
      countrycode: code,
    });
  };

  // State change handlers
  const handleAddStateChange = (stateId) => {
    // Clear city selection when state changes
    addForm.setFieldsValue({ city: undefined });
    fetchCities(stateId);
  };

  const handleEditStateChange = (stateId) => {
    // Clear city selection when state changes
    editForm.setFieldsValue({ city: undefined });
    fetchCities(stateId);
  };

  const handleFilterStateChange = (stateName) => {
    // Clear city selection when state changes in filter
    filterForm.setFieldsValue({ city: "" });
    fetchCities(stateName);
    // For filter, we might need to fetch cities based on state name
    // This depends on your API structure
  };

  const handleFilterCityChange = (stateName) => {
    // Clear city selection when state changes in filter
    fetchCities(stateName);
    // For filter, we might need to fetch cities based on state name
    // This depends on your API structure
  };

  // API Functions
  const fetchCategoryStats = async (contactsData = null) => {
    if (!currentAddressBook?.bookid) {
      setStatsLoading(false);
      updateContactsStats({ loading: false });
      return;
    }

    try {
      setStatsLoading(true);
      updateContactsStats({ loading: true });

      let allContacts = [];

      // If contactsData is provided, use it instead of making another API call
      if (contactsData) {
        allContacts = contactsData;
      } else {
        // Only make API call if no contact data is provided
        const response = await axiosConfig.post("/addressbook/users", {
          bookid: currentAddressBook.bookid,
        });

        if (
          response.data?.statuscode === 200 ||
          response.data?.status === true
        ) {
          const entity = response.data?.entity || {};
          const rawAllContacts = entity.users || [];
          allContacts = normalizeContactDataWithUserData(rawAllContacts);
        }
      }

      const newCategoryStats = {
        general: allContacts.filter((c) => {
          const category = c.userdata_category || c.usercategory || "";
          return category.toLowerCase() === "general";
        }).length,
        premium: allContacts.filter((c) => {
          const category = c.userdata_category || c.usercategory || "";
          return category.toLowerCase() === "premium";
        }).length,
        vip: allContacts.filter((c) => {
          const category = c.userdata_category || c.usercategory || "";
          return category.toLowerCase() === "vip";
        }).length,
        corporate: allContacts.filter((c) => {
          const category = c.userdata_category || c.usercategory || "";
          return category.toLowerCase() === "corporate";
        }).length,
      };

      setCategoryStats(newCategoryStats);

      // CORE FIX: Do NOT overwrite totalContacts here - it should only be set in fetchContacts()
      // Only update context with category stats, do not touch the pagination total
      updateContactsStats({
        general: newCategoryStats.general,
        premium: newCategoryStats.premium,
        vip: newCategoryStats.vip,
        corporate: newCategoryStats.corporate,
        loading: false,
      });
    } catch (error) {
      console.error("Fetch category stats error:", error);
      updateContactsStats({ loading: false });
    } finally {
      setStatsLoading(false);
    }
  };

  // Ref that always holds the latest fetchContacts to avoid stale closures
  const fetchContactsRef = useRef(null);

  const fetchContacts = async (
    filters = activeFilters,
    page = currentPage,
    size = pageSize,
  ) => {
    // Don't fetch contacts if no address book is selected
    if (!currentAddressBook?.bookid) {
      setContacts([]);
      setFilteredContacts([]);
      setTotalContacts(0);
      setHasContacts(false);
      setColumns(buildDefaultColumns());
      setUserColumnsMeta([]);
      // Reset loading states when no address book is selected
      setDataLoading(false);
      setTableLoading(false);
      setStatsLoading(false);
      return;
    }

    try {
      setDataLoading(true);
      setTableLoading(true);

      const requestBody = {
        bookid: currentAddressBook.bookid,
        limit: size,
        offset: (page - 1) * size,
        searchby: filters.search || "",
        labelfilter: Array.isArray(filters.labelfilter)
          ? filters.labelfilter.join(",")
          : filters.labelfilter || "",
      };

      const response = await axiosConfig.post(
        "/addressbook/users",
        requestBody,
      );

      if (
        (response.data?.statuscode === 200 || response.data?.status === true) &&
        response.data?.entity
      ) {
        const entity = response.data.entity;
        // Handle the new API response structure where users are in entity.users
        const contactsData = entity.users || entity;
        const normalizedData = normalizeContactDataWithUserData(contactsData);

        // CORE FIX: Get total records from API response (server-side count of ALL records)
        // The API should return totalRecords which represents the complete dataset size
        const totalRecordsFromAPI = entity.totalRecords || 0;

        // Apply filters if provided (for client-side filtering when needed)
        let filteredData = normalizedData;

        // Apply search filter
        if (filters.search) {
          filteredData = filteredData.filter(
            (contact) =>
              contact.username
                ?.toLowerCase()
                .includes(filters.search.toLowerCase()) ||
              contact.emailid
                ?.toLowerCase()
                .includes(filters.search.toLowerCase()) ||
              contact.mobileno?.includes(filters.search) ||
              contact.address
                ?.toLowerCase()
                .includes(filters.search.toLowerCase()),
          );
        }

        // Apply mobile number filter
        if (filters.mobileno) {
          filteredData = filteredData.filter((contact) =>
            contact.mobileno?.includes(filters.mobileno),
          );
        }

        // Apply label filter (supports JSON array strings like "[\"favourite\",\"close\"]")
        if (filters.labelfilter && filters.labelfilter.length > 0) {
          const selectedRaw = (
            Array.isArray(filters.labelfilter)
              ? filters.labelfilter
              : String(filters.labelfilter)
                  .split(",")
                  .map((l) => l.trim())
                  .filter(Boolean)
          ).map((l) => l.toLowerCase());

          // Map friendly label names to canonical values (e.g., "Close Friend" -> "close")
          const selected = selectedRaw.map((l) => {
            const match = predefinedLabels.find(
              (pl) =>
                pl.value.toLowerCase() === l || pl.label.toLowerCase() === l,
            );
            return match ? match.value.toLowerCase() : l;
          });

          filteredData = filteredData.filter((contact) => {
            const contactLabels = [];

            // Consider common label fields, including API 'label'
            const labelFields = [
              "label",
              "labels",
              "userdata_labels",
              "userlabels",
            ];
            labelFields.forEach((field) => {
              if (contact[field]) {
                const val = contact[field];
                if (typeof val === "string") {
                  // Try parse JSON array string first
                  const s = val.trim();
                  let parsed = null;
                  if (s.startsWith("[") && s.endsWith("]")) {
                    try {
                      parsed = JSON.parse(s);
                    } catch (e) {
                      parsed = null;
                    }
                  }
                  if (Array.isArray(parsed)) {
                    contactLabels.push(...parsed);
                  } else {
                    contactLabels.push(
                      ...s
                        .split(",")
                        .map((label) => label.trim())
                        .filter(Boolean),
                    );
                  }
                } else if (Array.isArray(val)) {
                  contactLabels.push(...val);
                }
              }
            });

            if (contactLabels.length === 0) return false;

            const lowerContactLabels = contactLabels.map((l) =>
              String(l).toLowerCase(),
            );
            // Require ALL selected labels (AND semantics)
            return selected.every((sel) => lowerContactLabels.includes(sel));
          });
        }

        // CORE FIX: Store the current page data and use total from API for accurate pagination
        setContacts(normalizedData);
        setFilteredContacts(filteredData);
        // CRITICAL: Always use totalRecordsFromAPI (server-side total) for pagination
        // This ensures page navigation works correctly and shows accurate totals
        setTotalContacts(totalRecordsFromAPI);
        setCurrentPage(page);
        setPageSize(size);
        setActiveFilters(filters);
        setHasContacts(contactsData.length > 0 || totalRecordsFromAPI > 0);

        // Update category stats using the PAGINATED data for performance
        // Stats should only reflect data shown on current page to avoid duplicate API calls
        fetchCategoryStats(normalizedData);

        // Build columns dynamically from the actual data
        // Use userdataColumns from API response for reliable column definitions
        const apiUserdataColumns = entity.userdataColumns || [];
        if (normalizedData.length > 0 || apiUserdataColumns.length > 0) {
          const dynamicColumns = buildColumnsFromNormalizedData(normalizedData, apiUserdataColumns);
          setColumns(dynamicColumns);

          // Build userColumnsMeta for forms
          const columnsMeta = buildUserColumnsMeta(normalizedData, apiUserdataColumns);
          setUserColumnsMeta(columnsMeta);
        } else {
          setColumns(buildDefaultColumns());
          setUserColumnsMeta([]);
        }

        updateContactsStats({
          total: totalRecordsFromAPI, // CORE FIX: Use server-side total count
          active: normalizedData.length,
          loading: false,
        });

        // Fetch all labels from entire address book (not just current page)
        fetchAllLabelsFromAddressBook();
      } else {
        setContacts([]);
        setFilteredContacts([]);
        setTotalContacts(0);
        setHasContacts(false);
        setColumns(buildDefaultColumns());
        setUserColumnsMeta([]);
      }
    } catch (error) {
      console.error("Fetch contacts error:", error);
      setContacts([]);
      setFilteredContacts([]);
      setTotalContacts(0);
      setHasContacts(false);
      setColumns(buildDefaultColumns());
      setUserColumnsMeta([]);
      setStatsLoading(false); // Reset stats loading on error
    } finally {
      setDataLoading(false);
      setTableLoading(false); // This was missing!
    }
  };

  // Always keep the ref pointing to the latest fetchContacts (fixes stale closure in handleRefresh)
  fetchContactsRef.current = fetchContacts;

  // Refresh handler to reload contacts and stats with rate limiting
  // Uses refs to avoid stale closure issues with currentAddressBook and fetchContacts
  const handleRefresh = async () => {
    if (!currentUser?.companyid) {
      return;
    }
    
    const now = Date.now();
    
    // Prevent duplicate calls if already in progress
    if (refreshInProgressRef.current) {
      return;
    }
    
    // Check cooldown period (3 seconds)
    if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) {
      message.warning('Please wait a moment before refreshing again');
      return;
    }
    
    // Mark refresh as in progress
    refreshInProgressRef.current = true;
    lastRefreshRef.current = now;
    setRefreshCooldown(true);
    
    // Clear current data to force skeletal loading to show
    setContacts([]);
    setFilteredContacts([]);
    setTableLoading(true);
    setDataLoading(true);
    setStatsLoading(true);

    try {
      // Reset pagination to first page
      setCurrentPage(1);

      // Fetch fresh data using the ref value to avoid stale closure
      await fetchContactsRef.current({}, 1, pageSize);

      // Optional success message (commented out to avoid notification spam)
      // message.success('Contacts refreshed successfully!');
    } catch (error) {
      console.error("Refresh error:", error);
      // Set loading states to false in case of error
      setTableLoading(false);
      setDataLoading(false);
      setStatsLoading(false);
    } finally {
      refreshInProgressRef.current = false;
      
      // Start cooldown timer
      setTimeout(() => {
        setRefreshCooldown(false);
      }, REFRESH_COOLDOWN_MS);
    }
  };

  // Load contacts on component mount and when currentUser changes
  useEffect(() => {
    if (currentUser?.companyid) {
      fetchContacts();
      // fetchCategoryStats(); // Fetch category statistics
    }
    fetchStates(); // Fetch states list on component mount
  }, [currentUser]);

  // Apply local filters (for client-side filtering when needed)
  const applyLocalFilters = (contactsList = contacts, search = searchText) => {
    let filtered = contactsList;

    // Apply search filter locally if needed
    if (search && !activeFilters.search) {
      filtered = filtered.filter(
        (contact) =>
          contact.username?.toLowerCase().includes(search.toLowerCase()) ||
          contact.emailid?.toLowerCase().includes(search.toLowerCase()) ||
          contact.mobileno?.includes(search) ||
          contact.address?.toLowerCase().includes(search.toLowerCase()) ||
          contact.usercategory?.toLowerCase().includes(search.toLowerCase()) ||
          contact.state?.toLowerCase().includes(search.toLowerCase()) ||
          contact.city?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    setFilteredContacts(filtered);
  };

  // Handle search
  const debouncedSearch = useCallback(
    async (searchValue) => {
      setSearchLoading(true);
      const newFilters = { ...activeFilters, search: searchValue };
      await fetchContacts(newFilters, 1, pageSize);
      setSearchLoading(false);
    },
    [activeFilters, pageSize],
  );

  const handleSearch = (value) => {
    setSearchText(value);

    // Clear the previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search is empty, immediately clear results and loading
    if (!value.trim()) {
      setSearchLoading(false);
      const newFilters = { ...activeFilters, search: "" };
      fetchContacts(newFilters, 1, pageSize);
      return;
    }

    // Show loading state for debouncing
    setSearchLoading(true);

    // Set a new timeout for debouncing (800ms delay for better UX)
    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 800);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle advanced filter
  const handleAdvancedFilter = () => {
    setAdvancedFilterVisible(true);
  };

  // Handle advanced filter form submission
  const handleAdvancedFilterSubmit = (values) => {
    // console.log(values);
    const newFilters = {
      search: values.search || "",
      mobileno: values.mobileno || "",
      labelfilter: values.labelfilter || [], // Support multiple labels
    };

    fetchContacts(newFilters, 1, pageSize);
    setAdvancedFilterVisible(false);
  };

  // Clear all filters
  const handleClearFilters = () => {
    const emptyFilters = {
      search: "",
      mobileno: "",
      labelfilter: [],
    };

    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Reset search loading state
    setSearchLoading(false);

    filterForm.resetFields();
    filterForm.setFieldsValue(emptyFilters);
    setSearchText("");
    fetchContacts(emptyFilters, 1, pageSize);
    setAdvancedFilterVisible(false);
    // message.success("Filters cleared successfully!");
  };

  // Handle pagination change
  const handleTableChange = (pagination) => {
    fetchContacts(activeFilters, pagination.current, pagination.pageSize);
  };

  // Load address books and dynamic table columns on mount
  useEffect(() => {
    fetchAddressBooks();
  }, []);

  // Fetch contacts when address book changes
  useEffect(() => {
    if (currentAddressBook) {
      fetchContacts();
    } else {
      setHasContacts(false);
      setContacts([]);
      setFilteredContacts([]);
      setColumns(buildDefaultColumns());
    }
  }, [currentAddressBook]);

  // Refetch contacts when page or pageSize changes (for custom pagination bar)
  useEffect(() => {
    if (currentAddressBook) {
      fetchContacts(activeFilters, currentPage, pageSize);
    }
  }, [currentPage, pageSize]);

  // Build columns when contacts data changes
  useEffect(() => {
    buildColumnsFromCurrentData();
  }, [contacts, currentPage, pageSize]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.userid, // Disable checkbox if no userid
    }),
  };

  // Data for the single table view
  const currentData = filteredContacts && filteredContacts.length > 0 ? filteredContacts : contacts;

  // Helper: generate a deterministic colour from a string
  const avatarColors = ["#0036b0","#0891b2","#7c3aed","#c026d3","#db2777","#ea580c","#16a34a","#ca8a04"];
  const getAvatarColor = (name) => {
    if (!name) return avatarColors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  const getNameInitials = (name = "") => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  // Columns for Excel-style table view
  const compactColumns = [
    {
      title: "#",
      dataIndex: "srno",
      key: "srno",
      width: 50,
      fixed: "left",
      render: (_, __, idx) => <span className="cp-cell-srno">{(currentPage - 1) * pageSize + idx + 1}</span>,
    },
    {
      title: "Name",
      dataIndex: "username",
      key: "username",
      width: 200,
      fixed: "left",
      render: (val) => {
        const initial = getNameInitials(val);
        return (
          <span className="cp-cell-name-wrap">
            <span className="cp-cell-avatar" style={{ backgroundColor: getAvatarColor(val) }}>{initial}</span>
            <span className="cp-cell-name">{val || "-"}</span>
          </span>
        );
      },
    },
    {
      title: "Mobile",
      dataIndex: "mobileno",
      key: "mobileno",
      width: 185,
      render: (val, record) => (
        <span className="cp-cell-phone-wrap">
          <PhoneCall size={12} className="cp-cell-inline-icon cp-cell-inline-icon--phone" />
          <span className="cp-cell-phone-inline">
            <span className="cp-cell-country-code">+{record?.countrycode || "--"}</span>
            <span className="cp-cell-phone-number">{val || "-"}</span>
          </span>
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "emailid",
      key: "emailid",
      width: 240,
      render: (val) => (
        <span className="cp-cell-email-wrap">
          <MailOpen size={13} className="cp-cell-inline-icon cp-cell-inline-icon--email" />
          <span className="cp-cell-email">{val || "-"}</span>
        </span>
      ),
    },
    {
      title: "Label",
      key: "label",
      width: 140,
      render: (_, record) => {
        // Handle both single label (string) and multiple labels (array or JSON string)
        let labelsArray = [];
        if (record.labels) {
          labelsArray = Array.isArray(record.labels) ? record.labels : [record.labels];
        } else if (record.label) {
          try {
            if (typeof record.label === "string" && record.label.startsWith("[")) {
              labelsArray = JSON.parse(record.label);
            } else {
              labelsArray = [record.label];
            }
          } catch (e) {
            labelsArray = [record.label];
          }
        }
        labelsArray = labelsArray.filter(Boolean);
        if (labelsArray.length === 0) {
          return <span className="cp-cell-label-empty">-</span>;
        }
        // Show first label + count if multiple
        const firstLabel = labelsArray[0];
        const labelInfo = predefinedLabels.find((l) => l.value === firstLabel);
        const displayName = labelInfo ? labelInfo.label : firstLabel;
        const moreCount = labelsArray.length > 1 ? labelsArray.length - 1 : 0;
        
        // Build tooltip content showing all labels
        const tooltipContent = (
          <div className="cp-labels-tooltip">
            {labelsArray.map((labelVal, idx) => {
              const info = predefinedLabels.find((l) => l.value === labelVal);
              return (
                <div key={idx} className="cp-labels-tooltip-item">
                  {info ? (
                    <>
                      <span className="cp-labels-tooltip-icon">{info.icon}</span>
                      <span>{info.label}</span>
                    </>
                  ) : (
                    <span>{labelVal}</span>
                  )}
                </div>
              );
            })}
          </div>
        );
        
        return (
          <Tooltip title={tooltipContent} placement="top" overlayClassName="cp-labels-tooltip-overlay">
            <span className="cp-cell-label" style={{ cursor: moreCount > 0 ? "pointer" : "default" }}>
              <Tag size="small" color="blue" className="cp-compact-tag">
                {displayName}
              </Tag>
              {moreCount > 0 && <span className="cp-label-more">+{moreCount}</span>}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <span className="cp-cell-actions">
          <Tooltip title="Edit" placement="top">
            <button
              className="cp-action-icon-btn cp-action-icon-btn--edit"
              onClick={() => handleEditContact(record)}
            >
              <SquarePen size={13} strokeWidth={2} />
            </button>
          </Tooltip>
          <span className="cp-cell-actions-sep" />
          <Popconfirm
            title="Delete Contact"
            description="Are you sure you want to delete this contact?"
            onConfirm={() => handleDeleteContact(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete" placement="top">
              <button className="cp-action-icon-btn cp-action-icon-btn--delete">
                <Trash2 size={13} strokeWidth={2} />
              </button>
            </Tooltip>
          </Popconfirm>
        </span>
      ),
    },
  ];

  // Derive dynamic userdata field definitions from columns state
  const dynamicUserdataFields = React.useMemo(() => {
    return columns.filter((col) => col.key && String(col.key).startsWith("userdata_"));
  }, [columns]);

  // Each userdata field becomes its own table column
  const extraFieldColumns = React.useMemo(() => {
    return dynamicUserdataFields.map((col) => ({
      title: col.title,
      key: col.key,
      dataIndex: col.dataIndex || col.key,
      width: 120,
      render: (val) => {
        if (val === undefined || val === null || val === "" || val === "-") {
          return <span className="cp-inline-extra-empty">—</span>;
        }
        return <span className="cp-extra-field-value">{val}</span>;
      },
    }));
  }, [dynamicUserdataFields]);

  // Final columns: #, Name, Mobile, Email + one col per extra field + Label + Actions
  const finalCompactColumns = React.useMemo(() => {
    const base = compactColumns.slice(0, 4); // #, Name, Mobile, Email
    const tail = compactColumns.slice(4);    // Label, Actions
    if (extraFieldColumns.length === 0) return [...base, ...tail];
    return [...base, ...extraFieldColumns, ...tail];
  }, [extraFieldColumns, compactColumns]);

  const stats = {
    total: totalContacts || 0, // Use total from API for accurate count across all pages
    general: categoryStats.general || 0,
    premium: categoryStats.premium || 0,
    vip: categoryStats.vip || 0,
  };
  const handleResetAddForm = () => {
    addForm.resetFields();
    setAddPhoneNumber("");
    setAddCountryCode("+91");
    setCitiesList([]); // Clear cities list
    fetchContacts(); // Refresh the list
    fetchCategoryStats(); // Refresh category statistics - this will update the context
  };

  // Handle functions
  const handleAddContact = async (values) => {
    // console.log(values);

    if (!currentUser?.companyid) {
      message.error("Company ID not found. Please log in again.");
      return;
    }

    if (!currentAddressBook?.bookid) {
      message.error("Please select an address book first.");
      return;
    }

    try {
      setLoading(true);

      // Build payload according to API specification
      const payload = {
        companyid: currentUser.companyid,
        bookid: currentAddressBook.bookid,
      };

      // Handle phone specially to extract local number and country code
      if (values.mobileno) {
        const codePlus = values.countrycode || "+91";
        const codeDigits = codePlus.replace("+", "");
        const full = values.mobileno;
        const localNumber = full.startsWith(codeDigits)
          ? full.slice(codeDigits.length)
          : full;
        payload.mobileno = localNumber;
        payload.countrycode = codeDigits;
      }

      // Standard/core fields - these go directly in the payload
      const coreFields = ["username", "emailid", "address"];
      coreFields.forEach((field) => {
        if (values[field] !== undefined && values[field] !== "") {
          payload[field] = values[field];
        }
      });

      // Validate only truly required fields (username, mobileno, emailid)
      if (!payload.username) {
        message.error("Please enter username");
        setLoading(false);
        return;
      }
      if (!payload.mobileno) {
        message.error("Please enter mobile number");
        setLoading(false);
        return;
      }
      if (!payload.emailid) {
        message.error("Please enter email");
        setLoading(false);
        return;
      }

      // Build userdata object for custom fields (userdata_ prefixed fields)
      const userdata = {};

      // Add any userdata_ prefixed fields to userdata object
      Object.keys(values).forEach((field) => {
        if (field.startsWith("userdata_")) {
          const fieldName = field.replace("userdata_", "");
          if (values[field] !== undefined && values[field] !== "") {
            userdata[fieldName] = values[field];
          }
        }
      });

      // Only include userdata if there are custom fields
      if (Object.keys(userdata).length > 0) {
        payload.userdata = userdata;
      }

      console.log("Add Contact Payload:", payload);

      const response = await axiosConfig.post("/contact/add", payload);

      if (response.data?.status) {
        switch (response.data.statuscode) {
          case 200:
            // Check if user was restored from soft delete
            if (response.data.entity?.restored) {
              message.success({
                content: (
                  <div>
                    <strong>User Restored!</strong>
                    <div style={{ fontSize: '12px', marginTop: '4px' }}>
                      {response.data.entity.message || 'Previously deleted user has been restored with updated information.'}
                    </div>
                  </div>
                ),
                duration: 5,
              });
            } else {
              // New user created successfully
              message.success(response.data.message || "Contact added successfully");
            }
            
            setAddContactVisible(false);
            handleResetAddForm();
            // Refresh contacts list (this will automatically update stats too)
            fetchContacts();
            // Refresh address books to update contact count in dropdown
            fetchAddressBooks();
            break;

          case 208:
            // Duplicate - active user already exists
            message.warning({
              content: response.data.message || "Mobile number already exists in this address book.",
              duration: 4,
            });
            break;

          case 403:
            // Validation error - missing required fields
            message.error({
              content: response.data.message || "All fields required: username, mobileno, countrycode, bookid.",
              duration: 4,
            });
            break;

          case 500:
            // Server error
            message.error({
              content: response.data.message || "Server error occurred. Please try again.",
              duration: 4,
            });
            break;

          default:
            // Unexpected status code
            message.error(response.data.message || "An unexpected error occurred");
            break;
        }
      } else {
        message.error(response.data?.message || "Failed to add contact");
      }
    } catch (error) {
      console.error("Add contact error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to add contact";
      message.error({
        content: errorMessage,
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);

    // Set up phone number for editing
    if (contact.mobileno && contact.countrycode) {
      const fullPhoneNumber = `+${contact.countrycode}${contact.mobileno}`;
      setEditPhoneNumber(fullPhoneNumber);
      setEditCountryCode(`+${contact.countrycode}`);
    } else {
      setEditPhoneNumber(contact.mobileno || "");
      setEditCountryCode("+91");
    }

    // Create dynamic field list based on available contact data
    const dynamicFields = [];
    const formValues = {};

    // Standard fields that are available in the response
    const standardFieldMap = {
      username: { label: "Full Name", type: "input", required: true },
      mobileno: { label: "Mobile Number", type: "phone", required: true },
      emailid: { label: "Email", type: "email", required: false },
      countrycode: {
        label: "Country Code",
        type: "countrycode",
        required: true,
      },
      address: { label: "Address", type: "textarea", required: false },
    };

    // Add standard fields that exist in contact data
    Object.keys(standardFieldMap).forEach((fieldKey) => {
      if (contact[fieldKey] !== undefined && contact[fieldKey] !== null) {
        dynamicFields.push({
          columnName: fieldKey,
          ...standardFieldMap[fieldKey],
        });

        // Set form values
        if (fieldKey === "mobileno" && contact.countrycode) {
          formValues[fieldKey] = `+${contact.countrycode}${contact[fieldKey]}`;
        } else if (fieldKey === "countrycode") {
          formValues[fieldKey] = `+${contact[fieldKey]}`;
        } else {
          formValues[fieldKey] = contact[fieldKey];
        }
      }
    });

    // Add userdata fields dynamically
    if (contact.userdata && typeof contact.userdata === "object") {
      Object.keys(contact.userdata).forEach((key) => {
        dynamicFields.push({
          columnName: `userdata_${key}`, // Use userdata_ prefix for form field name
          label: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          type: "input",
          required: false,
          isUserData: true,
        });
        formValues[`userdata_${key}`] = contact.userdata[key];
      });
    }

    // Set the dynamic fields for rendering
    setEditContactFields(dynamicFields);

    // Set form values
    editForm.setFieldsValue(formValues);

    setEditContactVisible(true);
  };

  const handleUpdateContact = async (values) => {
    if (!selectedContact?.userid) {
      message.error("Invalid contact information");
      return;
    }

    try {
      setLoading(true);

      // Build update payload dynamically
      // bookid MUST be included so the backend stores userdata at the per-book
      // level (addressbook_users.userdata) and not on the global users row.
      const updatePayload = {
        userid: selectedContact.userid,
        bookid: currentAddressBook?.bookid,
      };

      // Separate userdata from standard fields
      const userdata = {};
      const standardFields = [
        "username",
        "mobileno",
        "emailid",
        "countrycode",
        "address",
      ];

      // Handle phone number and country code extraction robustly
      if (values.mobileno) {
        let localNumber = values.mobileno;
        const countrycodeRaw = values.countrycode
          ? values.countrycode.replace("+", "")
          : "91";
        // Remove country code if present at start (with or without '+')
        if (localNumber.startsWith("+" + countrycodeRaw)) {
          localNumber = localNumber.slice(("+" + countrycodeRaw).length);
        } else if (localNumber.startsWith(countrycodeRaw)) {
          localNumber = localNumber.slice(countrycodeRaw.length);
        }
        updatePayload.mobileno = localNumber;
      }

      // Handle country code separately (it might be updated without mobile number)
      if (values.countrycode) {
        updatePayload.countrycode = values.countrycode.replace("+", "");
      }

      // Process all form values
      Object.keys(values).forEach((key) => {
        if (key === "mobileno" || key === "countrycode") {
          // Already handled above
          return;
        }

        if (values[key] !== undefined && values[key] !== null) {
          // Check if this field is a userdata field (starts with userdata_ prefix)
          if (key.startsWith("userdata_")) {
            // Strip the prefix and add to userdata object
            const fieldName = key.replace("userdata_", "");
            userdata[fieldName] = values[key];
          } else if (standardFields.includes(key)) {
            // Add as standard field
            updatePayload[key] = values[key];
          }
        }
      });

      // Add userdata object to payload if it has any fields
      if (Object.keys(userdata).length > 0) {
        updatePayload.userdata = userdata;
      }

      console.log("Update Contact Payload:", updatePayload);

      const response = await axiosConfig.post(
        "/addressbook/user/update",
        updatePayload,
      );

      if (response.data?.statuscode === 200) {
        message.success(
          response.data?.message || "Contact updated successfully",
        );
        setEditContactVisible(false);
        editForm.resetFields();
        setSelectedContact(null);
        setEditContactFields([]);
        setEditPhoneNumber("");
        setEditCountryCode("+91");
        setCitiesList([]); // Clear cities list
        fetchContacts(); // Refresh the list
        fetchCategoryStats(); // Refresh category statistics - this will update the context
      } else {
        message.error(response.data?.message || "Failed to update contact");
      }
    } catch (error) {
      console.error("Update contact error:", error);
      message.error(
        error.response?.data?.message || "Failed to update contact",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactOrUserid) => {
    // console.log("Deleting contact with ID:", contactId);
    const userid = typeof contactOrUserid === "object" ? contactOrUserid?.userid : contactOrUserid;
    const mobileno = typeof contactOrUserid === "object" ? contactOrUserid?.mobileno : undefined;

    if (!currentUser?.companyid) {
      message.error("Company ID not found. Please log in again.");
      return;
    }

    if (!currentAddressBook?.bookid) {
      message.error("Please select an address book first.");
      return;
    }

    // Prevent deletion if there's only 1 contact left in the address book
    if (totalContacts <= 1) {
      message.warning(
        "Cannot delete the last contact. At least one contact must remain in the address book.",
      );
      return;
    }

    try {
      const deletePayload = {
        userid: userid,
        bookid: currentAddressBook.bookid,
      };

      if (mobileno) {
        deletePayload.mobileno = mobileno;
      }

      const response = await axiosConfig.post("/addressbook/user/delete", deletePayload);

      if (response.data?.statuscode === 200) {
        message.success(
          response.data?.message || "Contact deleted successfully",
        );
        fetchContacts(); // Refresh the list
        fetchCategoryStats(); // Refresh category statistics - this will update the context
        fetchAddressBooks(); // Refresh address books to update contact count in dropdown
      } else {
        message.error(response.data?.message || "Failed to delete contact");
      }
    } catch (error) {
      console.error("Delete contact error:", error);
      message.error(
        error.response?.data?.message || "Failed to delete contact",
      );
    }
  };

  // Label assignment function - Updated to support multiple labels
  const handleRemoveLabelAssignment = async () => {
    if (!currentAddressBook?.bookid) { message.error('No address book selected'); return; }
    if (!selectedRowKeys.length) { message.error('No contacts selected'); return; }
    try {
      setRemoveLabelLoading(true);
      const response = await axiosConfig.post('/addressbook/remove-label', {
        bookid: currentAddressBook.bookid,
        userids: selectedRowKeys,
      });
      if (response.data?.statuscode === 200) {
        message.success(`Labels removed from ${selectedRowKeys.length} contact(s)`);
        setShowLabelModal(false);
        setSelectedLabel('');
        setCustomLabelName('');
        labelForm.resetFields();
        setSelectedRowKeys([]);
        await fetchContacts();
        await fetchCategoryStats();
        await fetchAllLabelsFromAddressBook();
      } else {
        message.error(response.data?.message || 'Failed to remove labels');
      }
    } catch (error) {
      console.error('Remove label error:', error);
      message.error(error.response?.data?.message || 'Failed to remove labels');
    } finally {
      setRemoveLabelLoading(false);
    }
  };

  const handleLabelAssignment = async (labelNames, userIds) => {
    if (!currentAddressBook?.bookid) {
      message.error("No address book selected");
      return;
    }

    if (!labelNames || labelNames.length === 0 || userIds.length === 0) {
      message.error("Please select labels and contacts");
      return;
    }

    try {
      setLabelLoading(true);

      const response = await axiosConfig.post("/addressbook/label-users", {
        bookid: currentAddressBook.bookid,
        labelnames: Array.isArray(labelNames) ? labelNames : [labelNames], // Support both single and multiple labels
        userids: userIds,
      });

      if (response.data?.statuscode === 200) {
        const labelText = Array.isArray(labelNames)
          ? labelNames.join(", ")
          : labelNames;
        message.success(
          `Labels "${labelText}" assigned to ${userIds.length} contact(s) successfully`,
        );
        setShowLabelModal(false);
        setSelectedLabel("");
        setCustomLabelName("");
        labelForm.resetFields();
        setSelectedRowKeys([]); // Clear selected contacts

        // Refresh contacts to show updated labels
        await fetchContacts();
        await fetchCategoryStats();

        // Also refresh all labels to show newly assigned custom labels
        await fetchAllLabelsFromAddressBook();
      } else {
        message.error(response.data?.message || "Failed to assign labels");
      }
    } catch (error) {
      console.error("Label assignment error:", error);
      message.error(error.response?.data?.message || "Failed to assign labels");
    } finally {
      setLabelLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select contacts first");
      return;
    }

    switch (action) {
      case "message":
        message.info(`Sending message to ${selectedRowKeys.length} contacts`);
        break;
      case "delete":
        // Check if deleting selected contacts would leave less than 1 contact
        if (totalContacts - selectedRowKeys.length < 1) {
          message.warning(
            "Cannot delete all contacts. At least one contact must remain in the address book.",
          );
          return;
        }

        try {
          // Show confirmation modal
          Modal.confirm({
            title: "Delete Selected Contacts",
            content: `Are you sure you want to delete ${selectedRowKeys.length} selected contact(s)? This action cannot be undone.`,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
              try {
                console.log("Selected Row Keys for deletion:", selectedRowKeys);

                // Since selectedRowKeys already contains user IDs (as shown in the log),
                // we can use them directly instead of trying to map from filteredContacts
                const userIds = selectedRowKeys.filter(Boolean); // Remove any undefined/null values

                console.log("User IDs to delete:", userIds);

                if (userIds.length === 0) {
                  message.error(
                    "No valid user IDs found for selected contacts",
                  );
                  return;
                }

                if (!currentUser?.companyid) {
                  message.error("Company ID not found. Please log in again.");
                  return;
                }

                // Double-check before deletion to prevent deleting all contacts
                if (totalContacts - userIds.length < 1) {
                  message.warning(
                    "Cannot delete all contacts. At least one contact must remain in the address book.",
                  );
                  return;
                }

                // Call delete API once with array of userids
                const response = await axiosConfig.post(
                  "/addressbook/user/delete",
                  {
                    userids: userIds,
                  },
                );

                if (response.data?.statuscode === 200) {
                  const deletedCount =
                    response.data.entity?.deletedCount || userIds.length;
                  message.success(
                    response.data?.message ||
                      `${deletedCount} contact(s) deleted successfully`,
                  );

                  // Clear selected rows
                  setSelectedRowKeys([]);

                  // Refresh the contacts list and stats
                  await Promise.all([fetchContacts(), fetchCategoryStats(), fetchAddressBooks()]);
                } else {
                  message.error(
                    response.data?.message || "Failed to delete contacts",
                  );
                }
              } catch (error) {
                console.error("Bulk delete error:", error);
                message.error(
                  error.response?.data?.message ||
                    "Failed to delete contacts. Please try again.",
                );
              }
            },
          });
        } catch (error) {
          console.error("Bulk delete confirmation error:", error);
          message.error("An error occurred. Please try again.");
        }
        break;
      case "label":
        setShowLabelModal(true);
        break;
      default:
        break;
    }
  };

  // Default field mapping structure - using database column names
  const defaultFields = [
    {
      key: "username",
      label: "Full Name",
      required: true,
      dbColumn: "username",
    },
    {
      key: "mobileno",
      label: "Mobile Number",
      required: true,
      dbColumn: "mobileno",
    },
    { key: "emailid", label: "Email", required: true, dbColumn: "emailid" },
    {
      key: "countrycode",
      label: "Country Code",
      required: true,
      dbColumn: "countrycode",
    },
    { key: "address", label: "Address", required: false, dbColumn: "address" },
  ];

  // Read file and extract headers
  const readFileHeaders = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let headers = [];
          let rows = [];

          if (file.name.endsWith(".csv")) {
            // Parse CSV
            const lines = data.split("\n");
            if (lines.length > 0) {
              headers = lines[0]
                .split(",")
                .map((h) => h.trim().replace(/"/g, ""));
              // Get first few rows for preview
              for (let i = 1; i < Math.min(6, lines.length); i++) {
                if (lines[i].trim()) {
                  const row = lines[i]
                    .split(",")
                    .map((cell) => cell.trim().replace(/"/g, ""));
                  rows.push(row);
                }
              }
            }
          } else {
            // Parse Excel
            const workbook = XLSX.read(data, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 0) {
              headers = jsonData[0] || [];
              rows = jsonData.slice(1, 6); // First 5 data rows for preview
            }
          }

          resolve({ headers, rows });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));

      if (file.name.endsWith(".csv")) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  // Handle file selection and header extraction
  const handleFileSelection = async (file) => {
    try {
      setUploading(true);
      const { headers, rows } = await readFileHeaders(file);

      setFileHeaders(headers);
      setFileData(rows);
      setSelectedFile(file);
      setShowMappingModal(true);
      setMappingStep(2); // Move to mapping step

      // Initialize default mapping with improved pattern matching
      const initialMapping = {};
      headers.forEach((header) => {
        const lowerHeader = header.toLowerCase().trim();

        // Auto-map to database column names with better pattern matching
        if (
          lowerHeader.includes("name") ||
          (lowerHeader.includes("full") && lowerHeader.includes("name")) ||
          lowerHeader === "name" ||
          (lowerHeader.includes("employee") && lowerHeader.includes("name")) ||
          (lowerHeader.includes("customer") && lowerHeader.includes("name")) ||
          (lowerHeader.includes("user") && lowerHeader.includes("name")) ||
          (lowerHeader.includes("contact") && lowerHeader.includes("name")) ||
          (lowerHeader.includes("person") && lowerHeader.includes("name"))
        ) {
          initialMapping.username = header;
        } else if (
          lowerHeader.includes("email") ||
          lowerHeader.includes("mail") ||
          lowerHeader === "email" ||
          lowerHeader === "e-mail" ||
          (lowerHeader.includes("email") && lowerHeader.includes("address"))
        ) {
          initialMapping.emailid = header;
        } else if (
          lowerHeader.includes("mobile") ||
          lowerHeader.includes("phone") ||
          (lowerHeader.includes("contact") && lowerHeader.includes("number")) ||
          lowerHeader === "mobile" ||
          lowerHeader === "phone" ||
          lowerHeader.includes("cell") ||
          lowerHeader.includes("telephone")
        ) {
          initialMapping.mobileno = header;
        } else if (
          (lowerHeader.includes("country") && lowerHeader.includes("code")) ||
          lowerHeader === "countrycode" ||
          lowerHeader === "country_code" ||
          (lowerHeader.includes("dial") && lowerHeader.includes("code")) ||
          (lowerHeader.includes("phone") && lowerHeader.includes("code"))
        ) {
          initialMapping.countrycode = header;
        } else if (
          lowerHeader.includes("address") ||
          lowerHeader === "address" ||
          lowerHeader.includes("location") ||
          lowerHeader.includes("street")
        ) {
          initialMapping.address = header;
        }
      });

      setHeaderMapping(initialMapping);
      message.success(
        `File "${file.name}" loaded successfully. ${headers.length} columns detected.`,
      );
    } catch (error) {
      console.error("File reading error:", error);
      message.error(
        "Failed to read file. Please ensure it's a valid CSV or Excel file.",
      );
    } finally {
      setUploading(false);
    }
  };

  // Handle final upload with mapping
  const handleMappedUpload = async () => {
    if (!currentUser?.companyid) {
      message.error("Company ID not found. Please log in again.");
      return;
    }

    // Address book name is now guaranteed to be present due to UI validation
    // No need for error popup - UI prevents reaching this point without address book name

    // Validate required fields are mapped (name, mobile, country code)
    const requiredFields = defaultFields.filter((f) => f.required);
    const missingRequired = requiredFields.filter(
      (field) => !headerMapping[field.key],
    );

    if (missingRequired.length > 0) {
      message.error(
        `Please map the following required fields: ${missingRequired.map((f) => f.label).join(", ")}`,
      );
      return;
    }

    try {
      setUploading(true);

      // Build defaultfield mapping for the API
      const defaultFieldMapping = {};
      defaultFields.forEach((field) => {
        if (headerMapping[field.key]) {
          defaultFieldMapping[field.dbColumn] = headerMapping[field.key];
        }
      });

      // Find unmapped columns (userdatafield)
      const mappedHeaders = Object.values(headerMapping);
      const unmappedHeaders = fileHeaders.filter(
        (header) => !mappedHeaders.includes(header),
      );

      // Determine address book name - use new name if creating, otherwise use current
      const bookName =
        newAddressBookName.trim() ||
        currentAddressBook?.bookname ||
        "My Address Book";

      // Create FormData with new API structure
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("defaultfield", JSON.stringify(defaultFieldMapping));
      formData.append("userdatafield", JSON.stringify(unmappedHeaders));
      formData.append("bookname", bookName);

      // Upload file with new API endpoint
      const response = await axiosConfig.post(
        "/contact/upload-excel-addressbook",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.statuscode === 200 || (response.data?.status === true && response.data?.statuscode !== 403)) {
        const uploadResult = response.data?.entity;

        // Show upload queued message from API response
        Modal.success({
          title: "Upload Queued Successfully",
          content: (
            <div>
              <p style={{ marginBottom: 16 }}>
                <strong>
                  {response.data?.message ||
                    "Excel upload with address book queued successfully"}
                </strong>
              </p>

              {uploadResult && (
                <div
                  style={{
                    backgroundColor: "rgba(34, 197, 94, 0.06)",
                    padding: 16,
                    borderRadius: 6,
                    border: "1px solid rgba(34, 197, 94, 0.25)",
                  }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <strong>Upload Details:</strong>
                  </div>

                  {/* <div style={{ marginBottom: 4 }}>
                    <span style={{ color: "#64748b" }}>Upload ID:</span>{" "}
                    <code style={{ backgroundColor: "#fff", padding: "2px 4px", borderRadius: 2 }}>
                      {uploadResult.uploadId}
                    </code>
                  </div> */}

                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: "#64748b" }}>Address Book:</span>{" "}
                    <strong>{uploadResult.bookname}</strong>
                  </div>

                  <div style={{ marginBottom: 4 }}>
                    <span style={{ color: "#64748b" }}>File Size:</span>{" "}
                    <span>{uploadResult.fileSizeInMB}</span>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: "#64748b" }}>Status:</span>{" "}
                    <Tag color="processing">
                      {uploadResult.status?.toUpperCase()}
                    </Tag>
                  </div>

                  <div
                    style={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}
                  >
                    {uploadResult.message}
                  </div>
                </div>
              )}
            </div>
          ),
          width: 600,
          okText: "Close",
          onOk: async () => {
            // Reset states
            setShowMappingModal(false);
            setBulkUploadVisible(false);
            setSelectedFile(null);
            setFileHeaders([]);
            setHeaderMapping({});
            setFileData([]);
            setMappingStep(1);

            // Set hasContacts to true after successful upload
            setHasContacts(true);

            // Clear the new address book name input
            setNewAddressBookName("");

            // If API returned a book id/name, optimistically add/select it so UI updates immediately
            if (uploadResult?.bookid) {
              const newBook = {
                bookid: uploadResult.bookid,
                bookname: uploadResult.bookname || bookName,
                totaluser:
                  uploadResult.totaluser || uploadResult.totalUsers || 0,
              };

              // Insert or replace the book in local state so Select updates immediately
              setAddressBooks((prev) => {
                const filtered = (prev || []).filter(
                  (b) => b.bookid !== newBook.bookid,
                );
                return [newBook, ...filtered];
              });

              // Set current address book immediately
              setCurrentAddressBook(newBook);

              // Fetch contacts for the newly created address book directly (don't rely on state update timing)
              try {
                setDataLoading(true);
                setTableLoading(true);

                const requestBody = {
                  bookid: newBook.bookid,
                  limit: pageSize,
                  offset: 0,
                  searchby: "",
                  labelfilter: "",
                };

                const contactsResponse = await axiosConfig.post(
                  "/addressbook/users",
                  requestBody,
                );

                if (
                  (contactsResponse.data?.statuscode === 200 ||
                    contactsResponse.data?.status === true) &&
                  contactsResponse.data?.entity
                ) {
                  const entity = contactsResponse.data.entity;
                  const contactsData = entity.users || entity;
                  const normalizedData =
                    normalizeContactDataWithUserData(contactsData);
                  const totalRecordsFromAPI =
                    entity.totalRecords || entity.total || 0;

                  setContacts(normalizedData);
                  setFilteredContacts(normalizedData);
                  setTotalContacts(totalRecordsFromAPI);
                  setCurrentPage(1);
                  setPageSize(pageSize);
                  setActiveFilters({
                    search: "",
                    mobileno: "",
                    labelfilter: [],
                  });
                  setHasContacts(
                    contactsData.length > 0 || totalRecordsFromAPI > 0,
                  );

                  // Update category stats and fetch labels via API
                  fetchCategoryStats(normalizedData);
                  await fetchAllLabelsFromAddressBook(newBook.bookid);

                  const apiUserdataColumns = entity.userdataColumns || [];
                  if (normalizedData.length > 0 || apiUserdataColumns.length > 0) {
                    const dynamicColumns =
                      buildColumnsFromNormalizedData(normalizedData, apiUserdataColumns);
                    setColumns(dynamicColumns);
                    const columnsMeta = buildUserColumnsMeta(normalizedData, apiUserdataColumns);
                    setUserColumnsMeta(columnsMeta);
                  }

                  updateContactsStats({
                    total: totalRecordsFromAPI,
                    active: normalizedData.length,
                    loading: false,
                  });
                }
                // Refresh address books to update contact count in dropdown
                fetchAddressBooks();
              } catch (error) {
                console.error("Error fetching contacts after upload:", error);
              } finally {
                setDataLoading(false);
                setTableLoading(false);
              }
            } else {
              // Fallback: refresh address book list from server
              try {
                setAddressBookLoading(true);
                const booksResponse = await axiosConfig.get(
                  "/addressbook/details",
                );
                if (
                  booksResponse.data?.statuscode === 200 &&
                  booksResponse.data?.entity
                ) {
                  const updatedBooks = booksResponse.data.entity;
                  setAddressBooks(updatedBooks);

                  // Select the newly created address book (should be the first one or latest)
                  if (updatedBooks.length > 0) {
                    const selectedBook = updatedBooks[0];
                    setCurrentAddressBook(selectedBook);

                    // Fetch its contacts
                    const requestBody = {
                      bookid: selectedBook.bookid,
                      limit: pageSize,
                      offset: 0,
                      searchby: "",
                      labelfilter: "",
                    };

                    const contactsResponse = await axiosConfig.post(
                      "/addressbook/users",
                      requestBody,
                    );
                    if (
                      (contactsResponse.data?.statuscode === 200 ||
                        contactsResponse.data?.status === true) &&
                      contactsResponse.data?.entity
                    ) {
                      const entity = contactsResponse.data.entity;
                      const contactsData = entity.users || entity;
                      const normalizedData =
                        normalizeContactDataWithUserData(contactsData);

                      setContacts(normalizedData);
                      setFilteredContacts(normalizedData);
                      setTotalContacts(
                        entity.totalRecords || entity.total || 0,
                      );
                      await fetchCategoryStats(normalizedData);

                      // Fetch all labels from the newly uploaded contacts
                      await fetchAllLabelsFromAddressBook();
                    }
                  }
                }
              } catch (error) {
                console.error("Error refreshing address books:", error);
              } finally {
                setAddressBookLoading(false);
              }
            }
          },
        });
      } else {
        message.error(
          response.data?.message || "Upload failed. Please try again.",
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error(
        error.response?.data?.message || "Upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  };

  // Upload configuration for new flow
  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    disabled: !newAddressBookName.trim(), // Disable upload until address book name is provided
    beforeUpload: (file) => {
      // Check if address book name is provided
      if (!newAddressBookName.trim()) {
        message.warning("Please enter an address book name first!");
        return false;
      }

      // Validate file type
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".xlsx");

      if (!isCSV && !isExcel) {
        message.error("You can only upload CSV or Excel files!");
        return false;
      }

      // Validate file size (5MB limit)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("File must be smaller than 5MB!");
        return false;
      }

      // Process file immediately
      handleFileSelection(file);
      return false; // Prevent default upload behavior
    },
    onRemove: () => {
      setSelectedFile(null);
      setFileHeaders([]);
      setHeaderMapping({});
      setFileData([]);
    },
    fileList: selectedFile
      ? [
          {
            uid: "1",
            name: selectedFile.name,
            status: "done",
          },
        ]
      : [],
  };

  // Handle upload button click (now for mapping modal)
  const handleUploadClick = async () => {
    if (mappingStep === 2) {
      setMappingStep(3); // Move to review step
    } else if (mappingStep === 3) {
      await handleMappedUpload();
    }
  };

  // Reset upload flow
  const resetUploadFlow = () => {
    setSelectedFile(null);
    setFileHeaders([]);
    setHeaderMapping({});
    setFileData([]);
    setMappingStep(1);
    setShowMappingModal(false);
  };

  return (
    <div className="cp-contacts-outer">
      {/* ── Page Header — outside card, matches Templates/Broadcast ── */}
      <div className="cp-page-header">
        <div>
          <h2 className="cp-page-title">Contacts</h2>
          <p className="cp-page-subtitle">Manage your contacts and address books</p>
        </div>
      </div>

    <div className="cp-contacts-page cp-grid-mode">
      {/* ── Unified Toolbar ── */}
      <div className="cp-topbar">
        <div className="cp-topbar-left">
          <div className="cp-directory-group">
            <div className="cp-directory-select-wrap">
              {addressBooks.length > 0 ? (
                <Select
                  className="contacts-address-select address-book-select"
                  value={currentAddressBook?.bookid}
                  onChange={(value) => {
                    const book = addressBooks.find((b) => b.bookid === value);
                    switchAddressBook(book);
                  }}
                  style={{ minWidth: 200 }}
                  loading={addressBookLoading}
                  placeholder="Select directory"
                  dropdownClassName="contacts-select"
                  size="small"
                >
                  {addressBooks.map((book) => (
                    <Option key={book.bookid} value={book.bookid}>
                      <span className="cp-contacts-dir-name">{book.bookname}</span>
                    </Option>
                  ))}
                </Select>
              ) : addressBookLoading ? (
                <AddressBookSkeleton />
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>No address books</Text>
              )}
            </div>

            <Tooltip title="Manage directories">
              <Button
                className="cp-btn-icon-sm"
                size="small"
                icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>settings</span>}
                onClick={() => { fetchAddressBooks(); setShowAddressBookModal(true); }}
                disabled={addressBookLoading}
              />
            </Tooltip>
          </div>

          <span className="cp-topbar-contact-count">
            &bull; {(currentAddressBook?.totaluser ?? totalContacts).toLocaleString()} contacts
          </span>
        </div>

        <div className="cp-topbar-right">
          <Input
            className="contacts-search-input cp-topbar-search"
            placeholder="Search…"
            size="small"
            prefix={
              searchLoading ? (
                <LoadingOutlined className="contacts-icon-theme" />
              ) : (
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>search</span>
              )
            }
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
          />

          <Badge
            className="contacts-badge"
            count={
              Object.values(activeFilters).filter((value) => {
                if (Array.isArray(value)) return value.length > 0;
                return value && value !== "";
              }).length
            }
            size="small"
          >
            <Tooltip title="Filters">
              <Button
                className={`cp-btn-icon-sm ${(() => {
                  const hasFilters = Object.values(activeFilters).some((value) => {
                    if (Array.isArray(value)) return value.length > 0;
                    return value && value !== "";
                  });
                  return hasFilters ? "cp-btn-icon-sm--active" : "";
                })()}`}
                size="small"
                icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>filter_list</span>}
                onClick={handleAdvancedFilter}
              />
            </Tooltip>
          </Badge>

          <Tooltip title={refreshCooldown ? "Please wait" : "Refresh"}>
            <Button
              className="cp-btn-icon-sm"
              size="small"
              icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>}
              onClick={handleRefresh}
              loading={tableLoading}
              disabled={tableLoading || refreshCooldown}
            />
          </Tooltip>

          {(() => {
            const hasActiveFilters = Object.values(activeFilters).some((value) => {
              if (Array.isArray(value)) return value.length > 0;
              return value && value !== "";
            });
            const shouldShowImportButton =
              currentAddressBook && !hasContacts && !hasActiveFilters;
            return shouldShowImportButton ? (
              <Tooltip title="Import Contacts">
                <Button
                  className="cp-btn-icon-sm"
                  size="small"
                  icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>}
                  disabled={tableLoading}
                  onClick={() => setBulkUploadVisible(true)}
                />
              </Tooltip>
            ) : null;
          })()}

          {selectedRowKeys.length > 0 && (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="label"
                    onClick={() => handleBulkAction("label")}
                    style={{ color: "var(--cp-primary)" }}
                  >
                    <TagOutlined /> Add Label
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    key="delete"
                    danger
                    onClick={() => handleBulkAction("delete")}
                  >
                    Delete Selected
                  </Menu.Item>
                </Menu>
              }
            >
              <Button className="cp-btn-icon-sm cp-btn-icon-sm--accent" size="small">
                Bulk
              </Button>
            </Dropdown>
          )}

          {hasContacts && currentAddressBook && (
            <WriteGuard page="contacts">
              <Tooltip title="Import from file">
                <Button
                  className="cp-btn-icon-sm"
                  size="small"
                  icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>upload_file</span>}
                  onClick={() => setBulkUploadVisible(true)}
                />
              </Tooltip>
            </WriteGuard>
          )}

          {hasContacts && currentAddressBook && (
            <WriteGuard page="contacts">
              <Button
                className="cp-btn-add-sm"
                type="primary"
                size="small"
                icon={<span className="material-symbols-outlined" style={{ fontSize: 16 }}>person_add</span>}
                disabled={tableLoading || !currentAddressBook}
                onClick={() => { setAddContactVisible(true); handleResetAddForm(); }}
              >
                Add
              </Button>
            </WriteGuard>
          )}
        </div>
      </div>

      {/* ── Dual-Pane Excel Table ── */}
      <div className="cp-grid-table-wrap">
        <BoneSkeleton
          name="contacts-table"
          loading={(tableLoading || addressBookLoading) && filteredContacts.length === 0}
          fallback={
            <div className="cp-contacts-table-skeleton">
              <TableSkeleton />
            </div>
          }
        >
          <>
            {currentAddressBook && (contacts.length > 0 || filteredContacts.length > 0) ? (
              <div className="cp-single-pane">
                <Table
                  className="contacts-table cp-excel-table"
                  rowSelection={rowSelection}
                  columns={finalCompactColumns}
                  dataSource={currentData}
                  rowKey={(record, index) =>
                    record.userid || record.id || `row-${index}`
                  }
                  loading={dataLoading}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content', y: 'calc(100vh - 216px)' }}
                />
              </div>
            ) : (
              <div className="contacts-empty-state">
                <div className="contacts-empty-graphic">
                  <UsergroupAddOutlined className="contacts-empty-icon" />
                </div>
                <div className="contacts-empty-title">
                  {!currentAddressBook
                    ? "No directory selected"
                    : searchText || Object.keys(activeFilters).length > 0
                      ? "No matching contacts"
                      : "No contacts yet"}
                </div>
                <div className="contacts-empty-desc">
                  {!currentAddressBook
                    ? "Select a directory or create one to start importing contacts."
                    : searchText || Object.keys(activeFilters).length > 0
                      ? "Try adjusting your search or filters."
                      : `Import contacts into "${currentAddressBook.bookname}" from a CSV or Excel file.`}
                </div>
                {!searchText &&
                  !Object.values(activeFilters).some((value) => {
                    if (Array.isArray(value)) return value.length > 0;
                    return value && value !== "";
                  }) && (
                    <Button
                      className="cp-btn-cta"
                      type="primary"
                      size="large"
                      icon={<UploadOutlined />}
                      onClick={() => setBulkUploadVisible(true)}
                    >
                      {!currentAddressBook ? "Create directory" : "Import contacts"}
                    </Button>
                  )}
              </div>
            )}
          </>
        </BoneSkeleton>

        {/* Bottom Pagination Bar */}
        {currentAddressBook && currentData.length > 0 && (
          <div className="cp-pagination-bar">
            <span className="cp-pagination-info">
              Showing {currentData.length} of {totalContacts.toLocaleString()} contacts
            </span>
            <div className="cp-pagination-controls">
              <span>Rows:</span>
              <Select
                size="small"
                value={pageSize}
                onChange={(val) => {
                  setPageSize(val);
                  setCurrentPage(1);
                }}
                options={[
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                  { value: 200, label: "200" },
                  { value: 500, label: "500" },
                  { value: 1000, label: "1000" },
                ]}
                style={{ width: 70 }}
              />
              <Button
                size="small"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <span className="cp-pagination-page">
                Page {currentPage} of {Math.ceil(totalContacts / pageSize) || 1}
              </span>
              <Button
                size="small"
                disabled={currentPage >= Math.ceil(totalContacts / pageSize)}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      <Modal
        title={null}
        open={addContactVisible}
        onCancel={() => setAddContactVisible(false)}
        footer={null}
        centered
        width={460}
        className="cpc-modal cpc-modal--add"
        wrapClassName="cpc-modal-wrap"
      >
        <div className="cpc-header cpc-header--gradient">
          <div className="cpc-header-icon-wrap">
            <span className="material-symbols-outlined" style={{ fontSize: 28, color: '#0036b0' }}>person_add</span>
          </div>
          <div className="cpc-header-title">Add New Contact</div>
          <div className="cpc-header-desc">
            Adding to <strong>{currentAddressBook?.addressbookname || "Default Address Book"}</strong> &bull; Only fields available in this address book are shown below.
          </div>
        </div>

        <Form form={addForm} layout="vertical" onFinish={handleAddContact} className="cpc-form">
          {/* Hidden field to store country code for phone input */}
          <Form.Item name="countrycode" hidden initialValue="+91" />

          <Row gutter={[16, 0]}>
            {userColumnsMeta.length > 0 ? (
              userColumnsMeta
                .filter(
                  (f) =>
                    ![
                      "userid",
                      "companyid",
                      "entrytime",
                      "addressbookid",
                      "countrycode",
                    ].includes(f.columnName),
                )
                .map((field) => (
                  <Col
                    xs={24}
                    sm={field.columnName === "address" ? 24 : 12}
                    key={field.columnName}
                  >
                    {renderDynamicFormItem(field)}
                  </Col>
                ))
            ) : (
              <>
                <Col xs={24} sm={12}>
                  {renderDynamicFormItem({
                    columnName: "username",
                    dataType: "character varying",
                  })}
                </Col>
                <Col xs={24} sm={12}>
                  {renderDynamicFormItem({
                    columnName: "mobileno",
                    dataType: "character varying(20)",
                  })}
                </Col>
                <Col xs={24} sm={12}>
                  {renderDynamicFormItem({
                    columnName: "emailid",
                    dataType: "character varying(255)",
                  })}
                </Col>
              </>
            )}
          </Row>

          <div className="cpc-footer">
            <Button onClick={() => setAddContactVisible(false)} className="cpc-btn-cancel">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="cpc-btn-submit"
            >
              Add Contact
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit Contact Modal */}
      <Modal
        title={null}
        open={editContactVisible}
        onCancel={() => {
          setEditContactVisible(false);
          setEditContactFields([]);
          setSelectedContact(null);
          editForm.resetFields();
        }}
        footer={null}
        centered
        width={640}
        className="cpc-modal"
      >
        <div className="cpc-header">
          <div className="cpc-header-title">Edit Contact</div>
          <div className="cpc-header-desc">
            Editing <strong>{editContactFields.length} field{editContactFields.length !== 1 ? "s" : ""}</strong> &bull; Only fields present in the contact data are displayed.
          </div>
        </div>

        <Form form={editForm} layout="vertical" onFinish={handleUpdateContact} className="cpc-form">
          <Row gutter={[16, 0]}>
            {editContactFields
              .filter(
                (f) =>
                  ![
                    "userid",
                    "companyid",
                    "entrytime",
                    "addressbookid",
                  ].includes(f.columnName),
              )
              .map((field) => (
                <Col
                  xs={24}
                  sm={field.columnName === "address" ? 24 : 12}
                  key={field.columnName}
                >
                  {renderDynamicEditFormItem(field)}
                </Col>
              ))}
          </Row>

          <div className="cpc-footer">
            <Button onClick={() => setEditContactVisible(false)} className="cpc-btn-cancel">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="cpc-btn-submit"
            >
              Update Contact
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        className="cpi-modal"
        open={bulkUploadVisible}
        onCancel={() => {
          setBulkUploadVisible(false);
          resetUploadFlow();
        }}
        footer={null}
        width={640}
        style={{ maxWidth: 'calc(100vw - 24px)' }}
        destroyOnClose
      >
        <div className="cpi-header">
          <div className="cpi-header-title">Import Contacts</div>
          <div className="cpi-header-desc">Upload your CSV or Excel file and we'll help you map the columns to our contact structure.</div>
        </div>

        <div className="cpi-body">
          {/* Address Book Name */}
          <div className="cpi-field">
            <label className="cpi-label">
              Address Book Name <span className="cpi-required">*</span>
            </label>
            <Input
              className="cpi-input"
              placeholder="Enter address book name (e.g., Healthcare Contacts, E-commerce Leads)"
              value={newAddressBookName}
              onChange={(e) => setNewAddressBookName(e.target.value)}
              status={!newAddressBookName.trim() ? "error" : ""}
              autoFocus
            />
            {!newAddressBookName.trim() && (
              <div className="cpi-hint-error">
                Address book name is required before uploading contacts
              </div>
            )}
          </div>

          {/* File Upload */}
          <div className={`cpi-field ${!newAddressBookName.trim() ? 'cpi-field--disabled' : ''}`}>
            <label className="cpi-label">Upload File</label>
            <Dragger className="cpi-dragger" {...uploadProps}>
              <div className="cpi-dragger-content">
                <InboxOutlined style={{ color: selectedFile ? '#0036b0' : '#cbd5e1', fontSize: 26 }} />
                {selectedFile ? (
                  <>
                    <div className="cpi-dragger-filename">✅ {selectedFile.name}</div>
                    <div className="cpi-dragger-meta">
                      {fileHeaders.length > 0 ? `${fileHeaders.length} columns detected • Ready to map` : 'Processing file...'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="cpi-dragger-text">Click or drag file to upload</div>
                    <div className="cpi-dragger-meta">Support for CSV and Excel files • Maximum size: 5MB</div>
                  </>
                )}
              </div>
            </Dragger>
          </div>

          {/* Sample Files */}
          <div className="cpi-samples">
            <span className="cpi-samples-label">Need help getting started?</span>
            <div className="cpi-samples-btns">
              <Button
                icon={<DownloadOutlined />}
                href="/assets/samples/sample_contacts_new.csv"
                download="sample_contacts_new.csv"
                className="cpi-btn-sample"
              >
                Sample CSV
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                href="/assets/samples/sample_contacts_new.xlsx"
                download="sample_contacts_new.xlsx"
                className="cpi-btn-sample cpi-btn-sample--excel"
              >
                Sample Excel
              </Button>
            </div>
          </div>
        </div>

        <div className="cpi-footer">
          <Button
            onClick={() => { setBulkUploadVisible(false); resetUploadFlow(); }}
            className="cpi-btn-cancel"
          >
            Cancel
          </Button>
          {selectedFile && fileHeaders.length > 0 && (
            <Button
              type="primary"
              onClick={() => setShowMappingModal(true)}
              disabled={!newAddressBookName.trim()}
              className="cpi-btn-continue"
            >
              Map & Continue
            </Button>
          )}
        </div>
      </Modal>

      {/* Header Mapping Modal */}
      <Modal
        className="cpmap-modal"
        title={null}
        open={showMappingModal}
        onCancel={() => {
          setShowMappingModal(false);
          setMappingStep(2);
        }}
        footer={null}
        width={800}
        style={{ maxWidth: 'calc(100vw - 24px)' }}
        destroyOnClose
      >
        <div className="cpmap-header">
          <div className="cpmap-header-title">
            {mappingStep === 2 ? "Map Your Columns" : "Review & Import"}
          </div>
          <div className="cpmap-header-desc">
            {mappingStep === 2
              ? "Match your file columns to contact fields for accurate import"
              : "Review the mapping and data before finalizing your import"}
          </div>
        </div>

        {mappingStep === 2 && (
          <div className="cpmap-body">
            {(() => {
              const unmappedRequired = defaultFields.filter(
                (f) => f.required && !headerMapping[f.key],
              );
              const hasUnmappedRequired = unmappedRequired.length > 0;

              return hasUnmappedRequired ? (
                <div className="cpmap-alert cpmap-alert--error">
                  <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#dc2626' }}>error</span>
                  <div>
                    <div className="cpmap-alert-title">{unmappedRequired.length} Required Field{unmappedRequired.length > 1 ? "s" : ""} Not Mapped</div>
                    <div className="cpmap-alert-desc">
                      Please map: {unmappedRequired.map((f) => f.label).join(", ")}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cpmap-alert cpmap-alert--success">
                  <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#16a34a' }}>check_circle</span>
                  <div>
                    <div className="cpmap-alert-title">All Required Fields Mapped</div>
                    <div className="cpmap-alert-desc">You can now proceed to review your import.</div>
                  </div>
                </div>
              );
            })()}

            <Row gutter={[16, 12]}>
              <Col xs={24} sm={10}>
                <div className="cpmap-panel">
                  <div className="cpmap-panel-title">Your File Columns ({fileHeaders.length})</div>
                  <div className="cpmap-tags">
                    {fileHeaders.map((header, index) => (
                      <span key={index} className="cpmap-tag">{header}</span>
                    ))}
                  </div>
                </div>

                {fileData.length > 0 && (
                  <div className="cpmap-panel" style={{ marginTop: 12 }}>
                    <div className="cpmap-panel-title">Data Preview (First 5 rows)</div>
                    <div className="cpmap-preview">
                      <table>
                        <thead>
                          <tr>
                            {fileHeaders.map((header, index) => (
                              <th key={index}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {fileData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {fileHeaders.map((_, colIndex) => (
                                <td key={colIndex}>{row[colIndex] || ""}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Col>

              <Col xs={24} sm={14}>
                <div className="cpmap-panel">
                  <div className="cpmap-panel-title">Map to Contact Fields</div>
                  <div className="cpmap-fields">
                    {defaultFields.map((field) => {
                      const isMapped = !!headerMapping[field.key];
                      const showError = field.required && !isMapped;

                      return (
                        <div
                          key={field.key}
                          className={`cpmap-field ${showError ? "cpmap-field--error" : isMapped ? "cpmap-field--mapped" : ""}`}
                        >
                          <div className="cpmap-field-info">
                            <span className="cpmap-field-label">{field.label}</span>
                            <div className="cpmap-field-badges">
                              {field.required && (
                                <span className={`cpmap-badge ${showError ? "cpmap-badge--error" : "cpmap-badge--required"}`}>
                                  {showError ? "Required" : "Required"}
                                </span>
                              )}
                              {isMapped && (
                                <span className="cpmap-badge cpmap-badge--mapped">✓ Mapped</span>
                              )}
                            </div>
                          </div>
                          <Select
                            style={{ flex: 1, minWidth: 0 }}
                            size="small"
                            status={showError ? "error" : ""}
                            placeholder={`Select column`}
                            value={headerMapping[field.key]}
                            onChange={(value) =>
                              setHeaderMapping((prev) => ({
                                ...prev,
                                [field.key]: value,
                              }))
                            }
                            allowClear
                            className="cpmap-select"
                          >
                            {fileHeaders.map((header) => (
                              <Option key={header} value={header}>
                                {header}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      );
                    })}
                  </div>

                </div>
              </Col>
            </Row>

            <div className="cpmap-footer">
              <Button
                onClick={() => {
                  setShowMappingModal(false);
                  setMappingStep(2);
                }}
                className="cpmap-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => setMappingStep(3)}
                disabled={defaultFields
                  .filter((f) => f.required)
                  .some((field) => !headerMapping[field.key])}
                className="cpmap-btn-primary"
              >
                Review Import
              </Button>
            </div>
          </div>
        )}

        {mappingStep === 3 && (
          <div className="cpmap-body">
            <div className="cpmap-alert cpmap-alert--success">
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#16a34a' }}>check_circle</span>
              <div>
                <div className="cpmap-alert-title">Ready to Import</div>
                <div className="cpmap-alert-desc">Review the mapping and data before proceeding.</div>
              </div>
            </div>

            <Row gutter={20}>
              <Col span={12}>
                <div className="cpmap-panel">
                  <div className="cpmap-panel-title">Import Summary</div>
                  <div className="cpmap-summary">
                    <div className="cpmap-summary-row">
                      <span>File</span>
                      <span>{selectedFile?.name}</span>
                    </div>
                    <div className="cpmap-summary-row">
                      <span>Total Columns</span>
                      <span>{fileHeaders.length}</span>
                    </div>
                    <div className="cpmap-summary-row">
                      <span>Mapped Fields</span>
                      <span>{Object.keys(headerMapping).filter((key) => headerMapping[key]).length}</span>
                    </div>
                    <div className="cpmap-summary-row">
                      <span>Additional Columns</span>
                      <span>{fileHeaders.filter((header) => !Object.values(headerMapping).includes(header)).length}</span>
                    </div>
                    <div className="cpmap-summary-row">
                      <span>Estimated Records</span>
                      <span>{fileData.length} (preview)</span>
                    </div>
                  </div>
                </div>
              </Col>

              <Col span={12}>
                <div className="cpmap-panel">
                  <div className="cpmap-panel-title">Field Mapping</div>
                  <div className="cpmap-summary">
                    {defaultFields.map(
                      (field) =>
                        headerMapping[field.key] && (
                          <div key={field.key} className="cpmap-summary-row">
                            <span>{field.label}</span>
                            <span>{headerMapping[field.key]}</span>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <div className="cpmap-footer">
              <Button onClick={() => setMappingStep(2)} className="cpmap-btn-cancel">
                Back to Mapping
              </Button>
              <Button
                type="primary"
                loading={uploading}
                onClick={handleMappedUpload}
                className="cpmap-btn-primary"
              >
                {uploading ? "Importing..." : "Import Contacts"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Advanced Filter Modal */}
      <Modal
        title={null}
        open={advancedFilterVisible}
        onCancel={() => setAdvancedFilterVisible(false)}
        footer={null}
        width={640}
        destroyOnClose
        className="advanced-filter-modal cp-filter-modal-v2"
      >
        <div className="cpf-header">
          <div>
            {/* <div className="cpf-header-title">Filter Contacts</div> */}
            {/* <div className="cpf-header-desc">Narrow down your directory with filters below.</div> */}
          </div>
        </div>

        <Form
          form={filterForm}
          layout="vertical"
          onFinish={handleAdvancedFilterSubmit}
          initialValues={activeFilters}
          className="cpf-form"
        >
          <div className="cpf-fields">
            <Form.Item
              name="search"
              label="Name"
              className="cpf-field"
            >
              <Input
                placeholder="Search by name..."
                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                allowClear
                className="cpf-input"
              />
            </Form.Item>

            <Form.Item
              name="mobileno"
              label="Mobile Number"
              className="cpf-field"
            >
              <Input
                placeholder="Enter mobile number..."
                prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />}
                allowClear
                className="cpf-input"
              />
            </Form.Item>

            <Form.Item
              name="labelfilter"
              label="Labels"
              className="cpf-field"
            >
              <Select
                mode="multiple"
                placeholder="Select labels..."
                allowClear
                className="cpf-select"
                dropdownStyle={{ borderRadius: 12, padding: 4 }}
                maxTagCount="responsive"
                showSearch
                filterOption={(input, option) => {
                  const searchText = input.toLowerCase();
                  const optionValue = (option?.value || "").toLowerCase();
                  const optionLabel = (option?.label || "").toLowerCase();
                  return (
                    optionValue.includes(searchText) ||
                    optionLabel.includes(searchText)
                  );
                }}
              >
                {predefinedLabels.map((label) => (
                  <Option
                    key={label.value}
                    value={label.value}
                    label={label.label}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: '#64748b', fontSize: 14 }}>{label.icon}</span>
                      <span style={{ color: '#191C1E', fontSize: 13 }}>{label.label}</span>
                    </div>
                  </Option>
                ))}
                {availableLabels
                  .filter(
                    (label) =>
                      !predefinedLabels.some(
                        (predefined) => predefined.value === label,
                      ),
                  )
                  .map((label) => (
                    <Option key={label} value={label} label={label}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <TagOutlined style={{ color: '#64748b', fontSize: 14 }} />
                        <span style={{ color: '#191C1E', fontSize: 13 }}>{label}</span>
                      </div>
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </div>

          {/* Active Filters Display */}
          {(activeFilters.search ||
            activeFilters.mobileno ||
            (activeFilters.labelfilter &&
              activeFilters.labelfilter.length > 0)) && (
            <div className="cpf-active">
              <div className="cpf-active-title">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
                Active Filters
              </div>
              <div className="cpf-active-tags">
                {activeFilters.search && (
                  <span className="cpf-tag">
                    <UserOutlined style={{ fontSize: 12 }} />
                    {activeFilters.search}
                  </span>
                )}
                {activeFilters.mobileno && (
                  <span className="cpf-tag">
                    <PhoneOutlined style={{ fontSize: 12 }} />
                    {activeFilters.mobileno}
                  </span>
                )}
                {activeFilters.labelfilter &&
                  activeFilters.labelfilter.length > 0 &&
                  activeFilters.labelfilter.map((label, index) => {
                    const predefinedLabel = predefinedLabels.find(
                      (pl) => pl.value === label,
                    );
                    return (
                      <span className="cpf-tag" key={index}>
                        {predefinedLabel ? (
                          <>
                            <span style={{ fontSize: 12 }}>{predefinedLabel.icon}</span>
                            {predefinedLabel.label}
                          </>
                        ) : (
                          <>
                            <TagOutlined style={{ fontSize: 12 }} />
                            {label}
                          </>
                        )}
                      </span>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="cpf-footer">
            <Button
              onClick={handleClearFilters}
              className="cpf-btn-clear"
            >
              Clear All
            </Button>
            <div className="cpf-footer-right">
              <Button
                onClick={() => setAdvancedFilterVisible(false)}
                className="cpf-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="cpf-btn-apply"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      {/* Manage Address Books Modal */}
      <Modal
        title={null}
        open={showAddressBookModal}
        onCancel={() => setShowAddressBookModal(false)}
        footer={null}
        width={640}
        destroyOnClose
        className="cpm-modal"
      >
        <div className="cpm-header">
          <div className="cpm-header-title">Manage Directories</div>
          <div className="cpm-header-desc">
            Switch between directories, create new ones, or remove unused address books. {addressBooks.length} director{addressBooks.length === 1 ? "y" : "ies"} available.
          </div>
        </div>

        <div className="cpm-body">
          {addressBooks.length === 0 ? (
            <div className="cpm-empty">
              <span className="material-symbols-outlined" style={{ fontSize: 36, color: '#94a3b8' }}>folder_off</span>
              <div className="cpm-empty-text">No directories found</div>
              <div className="cpm-empty-sub">Create your first directory by importing contacts below</div>
            </div>
          ) : (
            <div className="cpm-list">
              {addressBooks.map((book) => {
                const isCurrent = currentAddressBook?.bookid === book.bookid;
                return (
                  <div
                    key={book.bookid}
                    className={`cpm-item ${isCurrent ? "cpm-item--active" : ""}`}
                  >
                    <div className={`cpm-avatar ${isCurrent ? "cpm-avatar--active" : ""}`}>
                      {book.bookname.charAt(0).toUpperCase()}
                    </div>
                    <div className="cpm-info">
                      <div className="cpm-name">
                        {book.bookname}
                        {isCurrent && <span className="cpm-badge">ACTIVE</span>}
                      </div>
                      <div className="cpm-meta">
                        {book.totaluser || 0} records &bull; Last modified {new Date(book.entrytime).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                    <div className="cpm-actions">
                      {!isCurrent && (
                        <Button
                          size="small"
                          className="cpm-btn-switch"
                          onClick={() => {
                            switchAddressBook(book);
                            setShowAddressBookModal(false);
                          }}
                        >
                          Switch
                        </Button>
                      )}
                      <Popconfirm
                        title="Delete Directory"
                        description={`Are you sure you want to delete "${book.bookname}"? This will permanently delete all contacts in this directory.`}
                        onConfirm={() => deleteAddressBook(book.bookid)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={addressBooks.length === 1}
                          className="cpm-btn-delete"
                        />
                      </Popconfirm>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="cpm-footer">
          <Button
            onClick={() => setShowAddressBookModal(false)}
            className="cpm-btn-cancel"
          >
            Close
          </Button>
          <Button
            type="primary"
            className="cpm-btn-new"
            onClick={() => {
              setShowAddressBookModal(false);
              setBulkUploadVisible(true);
            }}
          >
            + New Directory
          </Button>
        </div>
      </Modal>

      {/* Label Assignment Modal */}
      <Modal
        title="Add Label to Contacts"
        open={showLabelModal}
        onCancel={() => {
          setShowLabelModal(false);
          setSelectedLabel("");
          setCustomLabelName("");
          labelForm.resetFields();
        }}
        footer={null}
        width={520}
        destroyOnClose
        className="cpl-modal"
      >
        <div className="cpl-header-sub">
          Select Labels (You can select multiple - Max 4 labels per contact)
        </div>

        <div>
          {/* Show current label count for selected contacts */}
          <div
            
          >
            {/* <Text
              strong
              style={{ fontSize: 13, marginBottom: 8, display: "block", color: "#0f172a" }}
            >
              Current Label Status
            </Text> */}
            <div className="cpl-contact-list">
              {selectedRowKeys.map((contactId) => {
                const contact = contacts.find((c) => c.userid === contactId);
                if (!contact) return null;

                let existingLabels = [];
                if (contact.labels) {
                  existingLabels = Array.isArray(contact.labels)
                    ? contact.labels
                    : [contact.labels];
                } else if (contact.label) {
                  if (
                    typeof contact.label === "string" &&
                    contact.label.startsWith("[")
                  ) {
                    try {
                      existingLabels = JSON.parse(contact.label);
                    } catch (e) {
                      existingLabels = [contact.label];
                    }
                  } else {
                    existingLabels = [contact.label];
                  }
                }

                const labelCount = existingLabels.length;
                const isAtLimit = labelCount >= 4;

                return (
                  <div
                    key={contactId}
                    className="cpl-contact-row"
                  >
                    <Text className="cpl-contact-name">
                      {contact.name || contact.phone}
                    </Text>
                    <Text
                      className={isAtLimit ? "cpl-label-count cpl-label-count--limit" : "cpl-label-count cpl-label-count--ok"}
                    >
                      {labelCount}/4 labels{isAtLimit ? " (limit)" : ""}
                    </Text>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <Form
          form={labelForm}
          layout="vertical"
          onFinish={(values) => {
            // Handle multiple label selection
            const selectedLabels = values.labelTypes || [];
            const customLabel = values.customLabel;

            let finalLabels = [...selectedLabels];
            if (customLabel && customLabel.trim()) {
              finalLabels.push(customLabel.trim());
            }

            if (finalLabels.length === 0) {
              message.error("Please select at least one label");
              return;
            }

            // Check if any selected contact would exceed 4 labels limit
            const contactsExceedingLimit = [];
            selectedRowKeys.forEach((contactId) => {
              const contact = contacts.find((c) => c.id === contactId);
              if (contact) {
                let existingLabels = [];

                // Parse existing labels (same logic as in the Labels column)
                if (contact.labels) {
                  existingLabels = Array.isArray(contact.labels)
                    ? contact.labels
                    : [contact.labels];
                } else if (contact.label) {
                  if (
                    typeof contact.label === "string" &&
                    contact.label.startsWith("[")
                  ) {
                    try {
                      existingLabels = JSON.parse(contact.label);
                    } catch (e) {
                      existingLabels = [contact.label];
                    }
                  } else {
                    existingLabels = [contact.label];
                  }
                }

                // Check if adding new labels would exceed 4 labels limit
                const totalLabelsAfterAssignment = new Set([
                  ...existingLabels,
                  ...finalLabels,
                ]).size;
                if (totalLabelsAfterAssignment > 4) {
                  contactsExceedingLimit.push({
                    name: contact.name || contact.phone,
                    currentCount: existingLabels.length,
                    wouldBe: totalLabelsAfterAssignment,
                  });
                }
              }
            });

            if (contactsExceedingLimit.length > 0) {
              const contactNames = contactsExceedingLimit
                .map((c) => `${c.name} (${c.currentCount} → ${c.wouldBe})`)
                .join(", ");
              message.error(
                `Cannot assign labels. The following contacts would exceed the 4-label limit: ${contactNames}`,
              );
              return;
            }

            handleLabelAssignment(finalLabels, selectedRowKeys);
          }}
        >
          <Form.Item
            name="labelTypes"
            rules={[
              {
                validator: async (_, value) => {
                  const customLabel = labelForm.getFieldValue("customLabel");
                  const selectedLabels = value || [];
                  const hasCustomLabel = customLabel && customLabel.trim();
                  const totalLabels =
                    selectedLabels.length + (hasCustomLabel ? 1 : 0);

                  if (totalLabels === 0) {
                    return Promise.reject(
                      new Error(
                        "Please select at least one label or add a custom label",
                      ),
                    );
                  }

                  if (totalLabels > 4) {
                    return Promise.reject(
                      new Error("Maximum 4 labels can be applied to a contact"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            dependencies={["customLabel"]}
          >
            <Checkbox.Group
              style={{ width: "100%" }}
              onChange={() => {
                // Trigger validation for customLabel field when labelTypes changes
                labelForm.validateFields(["customLabel"]).catch(() => {});
              }}
            >
              <div
                className="cpl-checkbox-list"
              >
                {predefinedLabels.map((label) => (
                  <div
                    key={label.value}
                    className="cpl-checkbox-row"
                  >
                    <Checkbox value={label.value}>
                      <div
                        className="cpl-checkbox-label"
                      >
                        <span className="cpl-checkbox-icon">{label.icon}</span>
                        <span className="cpl-checkbox-text">
                          {label.label}
                        </span>
                      </div>
                    </Checkbox>
                    <span className="cpl-checkbox-desc">
                      {label.description}
                    </span>
                  </div>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="customLabel"
            label="Custom Label (Optional)"
            style={{ marginTop: 16 }}
            rules={[
              {
                validator: async (_, value) => {
                  const selectedLabels =
                    labelForm.getFieldValue("labelTypes") || [];
                  const hasCustomLabel = value && value.trim();
                  const totalLabels =
                    selectedLabels.length + (hasCustomLabel ? 1 : 0);

                  // Check for empty custom label with special characters or just spaces
                  if (value && !value.trim()) {
                    return Promise.reject(
                      new Error(
                        "Custom label cannot be empty or contain only spaces",
                      ),
                    );
                  }

                  // Check for minimum length if custom label is provided
                  if (hasCustomLabel && value.trim().length < 2) {
                    return Promise.reject(
                      new Error(
                        "Custom label must be at least 2 characters long",
                      ),
                    );
                  }

                  // Check for maximum length
                  if (hasCustomLabel && value.trim().length > 50) {
                    return Promise.reject(
                      new Error("Custom label cannot exceed 50 characters"),
                    );
                  }

                  // Check for invalid characters
                  if (
                    hasCustomLabel &&
                    !/^[a-zA-Z0-9\s\-_]+$/.test(value.trim())
                  ) {
                    return Promise.reject(
                      new Error(
                        "Custom label can only contain letters, numbers, spaces, hyphens, and underscores",
                      ),
                    );
                  }

                  // Check if custom label already exists in predefined labels
                  if (hasCustomLabel) {
                    const customLabelLower = value.trim().toLowerCase();
                    const existsInPredefined = predefinedLabels.some(
                      (label) =>
                        label.label.toLowerCase() === customLabelLower ||
                        label.value.toLowerCase() === customLabelLower,
                    );
                    if (existsInPredefined) {
                      return Promise.reject(
                        new Error(
                          "This label already exists in predefined labels. Please select it from the list above.",
                        ),
                      );
                    }
                  }

                  if (totalLabels > 4) {
                    return Promise.reject(
                      new Error("Maximum 4 labels can be applied to a contact"),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            dependencies={["labelTypes"]}
          >
            <Input
              placeholder="Enter custom label name (2-50 characters, letters, numbers, spaces, hyphens, underscores only)"
              prefix={<TagOutlined />}
              maxLength={50}
              onChange={() => {
                // Trigger validation for labelTypes field when customLabel changes
                labelForm.validateFields(["labelTypes"]).catch(() => {});
              }}
              onBlur={() => {
                // Validate current field on blur for immediate feedback
                labelForm.validateFields(["customLabel"]).catch(() => {});
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <div
              className="cpl-footer"
            >
              <Button
                className="cpl-btn-cancel"
                onClick={() => {
                  setShowLabelModal(false);
                  setSelectedLabel("");
                  setCustomLabelName("");
                  labelForm.resetFields();
                }}
              >
                Cancel
              </Button>
              {/* Show Remove button only if at least one selected contact has a label */}
              {selectedRowKeys.some(id => {
                const c = contacts.find(x => x.userid === id);
                if (!c) return false;
                const lbl = c.labels || c.label;
                if (!lbl) return false;
                const arr = Array.isArray(lbl) ? lbl : (typeof lbl === 'string' && lbl.startsWith('[') ? (() => { try { return JSON.parse(lbl); } catch { return [lbl]; } })() : [lbl]);
                return arr.filter(Boolean).length > 0;
              }) && (
                <Button
                  danger
                  loading={removeLabelLoading}
                  onClick={handleRemoveLabelAssignment}
                >
                  Remove Label
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                loading={labelLoading}
                className="cpl-btn-submit"
              >
                Assign Labels
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Additional Info Modal removed — data is now shown inline in the table column */}
    </div>
    </div>
  );
};

export default ContactsPage;
